import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { formatPilotMarkupLabel } from '../utils/pricingDisplay';
import { getPilotCertificateBlockers } from '../utils/pilotCertificateValidity';
import { pilotDocumentFetchUrl } from '../utils/pilotDocumentUrl';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line, CartesianGrid } from 'recharts';
import EmailTemplatesAdmin from '../components/EmailTemplatesAdmin';
import PilotPrivateCommentsPanel from '../components/PilotPrivateCommentsPanel';
import PilotMyProfilePanel from '../components/PilotMyProfilePanel';
import PilotProfileChangesQueue from '../components/PilotProfileChangesQueue';
import './AdminDashboard.css';

const ACCESS_TABS = new Set(['accessAdmin', 'accessPilots', 'accessCaa', 'accessOwners', 'accessPartners']);
const PLATFORM_CHART_COLORS = ['#0077C8', '#28a745', '#ffc107', '#6f42c1', '#e91e63', '#17a2b8', '#fd7e14'];

function getAccessRole(inst) {
  const r = inst?.accessRole || inst?.access_role;
  return r ? String(r).toLowerCase() : null;
}

// Booking item komponens – accordion: egy sor összefoglaló, kattintásra lenyílik
const BookingItem = ({
  booking,
  rowIndex,
  formatDate,
  formatHour,
  getPaymentMethodLabel,
  getPaymentStatusLabel,
  updatePaymentStatus,
  deleteBooking,
  updateBookingCheck,
  isCancelled = false,
  onArchive,
  onRestore
}) => {
  const { t, i18n } = useTranslation();
  const loc = useMemo(
    () => (i18n.language === 'hu' ? 'hu-HU' : i18n.language === 'he' ? 'he-IL' : 'en-GB'),
    [i18n.language]
  );
  const [expanded, setExpanded] = useState(false);
  const [isPaid, setIsPaid] = useState(booking.payment_status === 'paid');
  const [paymentInvoiceNumber, setPaymentInvoiceNumber] = useState(booking.payment_invoice_number || '');
  const [paymentDate, setPaymentDate] = useState(booking.payment_date || '');
  const [isChecked, setIsChecked] = useState(booking.checked === 1 || booking.checked === true);
  const [paymentLogs, setPaymentLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  useEffect(() => {
    if (booking.id && expanded) {
      loadPaymentLogs();
    }
  }, [booking.id, expanded]);

  const loadPaymentLogs = async () => {
    setLoadingLogs(true);
    try {
      const response = await axios.get(`/api/booking/booking/${booking.id}/payment-logs`);
      setPaymentLogs(response.data.logs || []);
    } catch (err) {
      console.error('Log load error:', err);
    } finally {
      setLoadingLogs(false);
    }
  };

  const handlePaymentStatusChange = async (checked) => {
    setIsPaid(checked);
    const newStatus = checked ? 'paid' : 'pending';
    await updatePaymentStatus(booking.id, newStatus, paymentInvoiceNumber, checked ? paymentDate : null);
    setTimeout(() => loadPaymentLogs(), 500); // Frissítjük a logokat
  };

  const handleInvoiceNumberChange = async (value) => {
    setPaymentInvoiceNumber(value);
    if (isPaid) {
      await updatePaymentStatus(booking.id, 'paid', value, paymentDate);
      setTimeout(() => loadPaymentLogs(), 500);
    }
  };

  const handlePaymentDateChange = async (value) => {
    setPaymentDate(value);
    if (isPaid) {
      await updatePaymentStatus(booking.id, 'paid', paymentInvoiceNumber, value);
      setTimeout(() => loadPaymentLogs(), 500);
    }
  };

  const handleCheckChange = async (checked) => {
    setIsChecked(checked);
    await updateBookingCheck(booking.id, checked);
  };

  const formatLogDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString(loc, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className={`booking-item booking-accordion ${expanded ? 'expanded' : ''}`}>
      {/* Összefoglaló sor – kattintásra lenyílik */}
      <div 
        className="booking-row-header"
        onClick={() => setExpanded(!expanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setExpanded(!expanded)}
      >
                <div className="booking-row-col booking-col-index">
          {rowIndex != null ? rowIndex + 1 : ''}
        </div>
        <div className="booking-row-col booking-col-created">
          {booking.created_at ? (() => {
            const d = new Date(booking.created_at);
            return d.toLocaleString(loc, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
          })() : '–'}
        </div>
        <div className="booking-row-col booking-col-datetime">
          <span className="booking-datetime">{formatDate(booking.date)}</span>
          <span className="booking-time">{formatHour(booking.hour)}</span>
        </div>
        <div className="booking-row-col booking-col-customer">
          <strong>{booking.customer_name}</strong>
          {booking.created_at && (Date.now() - new Date(booking.created_at).getTime()) < 24 * 60 * 60 * 1000 && (
            <span className="new-booking-badge">{t('admin.bookingItem.new')}</span>
          )}
        </div>
        {booking.instructor_name && (
          <div className="booking-row-col booking-col-instructor">
            <span className="instructor-badge">{booking.instructor_name}</span>
          </div>
        )}
        {(booking.aircraft_registration || booking.aircraft_type) && (
          <div className="booking-row-col booking-col-instructor">
            <span className="instructor-badge">
              {[booking.aircraft_registration, booking.aircraft_type].filter(Boolean).join(' · ')}
            </span>
          </div>
        )}
        <div className="booking-row-col booking-col-method">
          {getPaymentMethodLabel(booking.payment_method)}
        </div>
        <div className="booking-row-col booking-col-amount">
          {booking.payment_amount ? `${parseInt(booking.payment_amount).toLocaleString(loc)} €` : '–'}
        </div>
        <div className="booking-row-col booking-col-status">
          {isCancelled ? (
            <span className="cancelled-badge-inline">{t('admin.bookingItem.cancelled')}</span>
          ) : (
            <>
              <span className={`payment-badge payment-badge-${booking.payment_status || 'pending'}`}>
                {getPaymentStatusLabel(booking.payment_status)}
              </span>
              {isChecked && <span className="checked-badge" title={t('admin.bookingItem.checked')}>✓</span>}
            </>
          )}
        </div>
        <div className="booking-row-col booking-col-expand">
          <span className="accordion-chevron">{expanded ? '▼' : '▶'}</span>
        </div>
      </div>
      {/* Részletek – lenyílik */}
      <div className="booking-row-details" onClick={(e) => e.stopPropagation()}>
        <div className="booking-info">
          <div className="booking-header-row">
            {!isCancelled && (
              <label className="checked-checkbox-label">
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e) => handleCheckChange(e.target.checked)}
                />
                <span>{t('admin.bookingItem.checked')}</span>
              </label>
            )}
          </div>
          {isChecked && booking.checked_at && (
            <div className="checked-info">
              <small>
                {t('admin.bookingItem.checkedLine', {
                  at: formatLogDate(booking.checked_at),
                  by: booking.checked_by_name ? t('admin.bookingItem.byName', { name: booking.checked_by_name }) : ''
                })}
              </small>
            </div>
          )}
          <p><strong>{t('admin.bookingItem.email')}</strong> {booking.customer_email}</p>
          {booking.customer_phone && <p><strong>{t('admin.bookingItem.phone')}</strong> {booking.customer_phone}</p>}
          {(booking.aircraft_registration || booking.aircraft_type) && (
            <p>
              <strong>{t('admin.aircraft.label', { defaultValue: 'Repülőgép' })}</strong>{' '}
              {[booking.aircraft_registration, booking.aircraft_type].filter(Boolean).join(' · ')}
            </p>
          )}
          {booking.notes && <p><strong>{t('admin.bookingItem.notes')}</strong> {booking.notes}</p>}
          {booking.passenger_weights_submitted_at && (() => {
            let w = booking.passenger_weights_json;
            if (typeof w === 'string') {
              try {
                w = JSON.parse(w);
              } catch {
                w = null;
              }
            }
            if (!Array.isArray(w) || w.length === 0) return null;
            return (
              <div className="passenger-weights-admin">
                <p><strong>{t('admin.bookingItem.passengerWeights')}</strong> {w.map((kg, i) => `${i + 1}: ${kg}`).join(' · ')}</p>
                {booking.passenger_weights_notes && (
                  <p><strong>{t('admin.bookingItem.weightNotes')}</strong> {booking.passenger_weights_notes}</p>
                )}
                <p><small>{t('admin.bookingItem.weightsSubmitted')} {formatLogDate(booking.passenger_weights_submitted_at)}</small></p>
              </div>
            );
          })()}
          <div className="payment-details">
            <p><strong>{t('admin.bookingItem.paymentMethod')}</strong> {getPaymentMethodLabel(booking.payment_method)}</p>
            {booking.payment_amount && <p><strong>{t('admin.bookingItem.amount')}</strong> {parseInt(booking.payment_amount).toLocaleString(loc)} €</p>}
          {!isCancelled && (
            <div className="payment-status-control">
              <label className="payment-checkbox-label">
                <input
                  type="checkbox"
                  checked={isPaid}
                  onChange={(e) => handlePaymentStatusChange(e.target.checked)}
                />
                <span>{t('admin.bookingItem.paid')}</span>
              </label>
              {isPaid && (
                <div className="payment-notes-section">
                  <div className="form-group">
                    <label>{t('admin.bookingItem.invoiceNumber')}</label>
                    <input
                      type="text"
                      value={paymentInvoiceNumber}
                      onChange={(e) => handleInvoiceNumberChange(e.target.value)}
                      placeholder={t('admin.bookingItem.invoicePh')}
                      className="payment-notes-input"
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('admin.bookingItem.paymentDate')}</label>
                    <input
                      type="date"
                      value={paymentDate}
                      onChange={(e) => handlePaymentDateChange(e.target.value)}
                      className="payment-notes-input"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          {isCancelled && booking.payment_status && (
            <div className="payment-status-control">
              <p><strong>{t('admin.bookingItem.paymentStatus')}</strong> 
                <span className={`payment-status payment-status-${booking.payment_status || 'pending'}`}>
                  {getPaymentStatusLabel(booking.payment_status)}
                </span>
              </p>
              {booking.payment_invoice_number && (
                <p><strong>{t('admin.bookingItem.invoiceNumber')}</strong> {booking.payment_invoice_number}</p>
              )}
              {booking.payment_date && (
                <p><strong>{t('admin.bookingItem.paymentDate')}</strong> {formatDate(booking.payment_date)}</p>
              )}
            </div>
          )}
          </div>
          {/* Fizetési logok */}
          {paymentLogs.length > 0 && (
            <div className="payment-logs-section">
              <h4>{t('admin.bookingItem.paymentLogTitle')}</h4>
              <div className="payment-logs-list">
                {paymentLogs.map((log) => (
                  <div key={log.id} className="payment-log-item">
                    <div className="log-header">
                      <span className="log-action">{log.action === 'PAID' ? t('admin.bookingItem.logPaid') : log.action === 'UNPAID' ? t('admin.bookingItem.logUnpaid') : t('admin.bookingItem.logCancelled')}</span>
                      <span className="log-date">{formatLogDate(log.created_at)}</span>
                    </div>
                    <div className="log-details">
                      {log.old_status !== log.new_status && (
                        <p>{t('admin.bookingItem.logStatus', { from: getPaymentStatusLabel(log.old_status), to: getPaymentStatusLabel(log.new_status) })}</p>
                      )}
                      {log.old_invoice_number !== log.new_invoice_number && (
                        <p>{t('admin.bookingItem.logInvoice', { from: log.old_invoice_number || '-', to: log.new_invoice_number || '-' })}</p>
                      )}
                      {log.old_payment_date !== log.new_payment_date && (
                        <p>{t('admin.bookingItem.logPayDate', {
                          from: log.old_payment_date ? formatDate(log.old_payment_date) : '-',
                          to: log.new_payment_date ? formatDate(log.new_payment_date) : '-'
                        })}</p>
                      )}
                      {log.changed_by_name && <p className="log-changed-by">{t('admin.bookingItem.changedBy', { name: log.changed_by_name })}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="booking-actions">
          {!isCancelled && onArchive && (
            <button className="btn btn-secondary btn-sm" onClick={() => onArchive(booking.id)}>{t('admin.bookingItem.archive')}</button>
          )}
          {!isCancelled && onRestore && (
            <button className="btn btn-primary btn-sm" onClick={() => onRestore(booking.id)}>{t('admin.bookingItem.restore')}</button>
          )}
          {!isCancelled && !onArchive && !onRestore && deleteBooking && (
            <button className="btn btn-danger btn-sm" onClick={() => deleteBooking(booking.id)}>{t('admin.bookingItem.delete')}</button>
          )}
          {isCancelled && <div className="cancelled-badge">{t('admin.bookingItem.cancelled')}</div>}
        </div>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { t, i18n } = useTranslation();
  const dateLocale = useMemo(
    () => (i18n.language === 'hu' ? 'hu-HU' : i18n.language === 'he' ? 'he-IL' : 'en-GB'),
    [i18n.language]
  );
  const numberLocale = dateLocale;

  const { instructor } = useAuth();
  const canViewDashboard = Boolean(instructor?.canViewDashboard || instructor?.isAdmin);
  const isSystemAdmin = Boolean(instructor?.isAdmin);
  const userAccessRole = instructor?.accessRole || null;
  const isCaaUser = userAccessRole === 'caa' && !isSystemAdmin;
  const canManageAccesses = isSystemAdmin;
  const canManageEmailTemplates = isSystemAdmin || userAccessRole === 'admin';
  const pilotStatusNorm = (instructor?.status || 'approved').toLowerCase();
  const pilotCertBlockers = useMemo(() => getPilotCertificateBlockers(instructor, t), [instructor, t]);
  const canEditAvailability =
    Boolean(instructor?.isAdmin) ||
    (pilotStatusNorm === 'approved' && pilotCertBlockers.length === 0);
  const [activeTab, setActiveTab] = useState('availability');
  const didMountScrollRef = useRef(false);
  const [bookingsSubTab, setBookingsSubTab] = useState('active'); // 'active' | 'cancelled' | 'calendar'
  const [calendarWeekFrom, setCalendarWeekFrom] = useState(() => {
    const d = new Date();
    const monday = new Date(d);
    monday.setDate(d.getDate() - d.getDay() + (d.getDay() === 0 ? -6 : 1));
    return monday.toISOString().slice(0, 10);
  });
  const [calendarData, setCalendarData] = useState({ days: [] });
  const [availability, setAvailability] = useState([]);
  const [currentInstructorId, setCurrentInstructorId] = useState(null);
  const [takenSlotsForDate, setTakenSlotsForDate] = useState({ date: '', details: [] });
  const [bookings, setBookings] = useState([]);
  const [cancelledBookings, setCancelledBookings] = useState([]);
  const [newBookingsCount, setNewBookingsCount] = useState(0);
  const [showNewBookingPopup, setShowNewBookingPopup] = useState(false);
  const [instructors, setInstructors] = useState([]);
  const [ownerAccounts, setOwnerAccounts] = useState([]);
  const [partnerCampaigns, setPartnerCampaigns] = useState([]);
  const [partnerAccounts, setPartnerAccounts] = useState([]);
  const [aircraftOptions, setAircraftOptions] = useState([]);
  const [selectedPilotAircraftIds, setSelectedPilotAircraftIds] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [pilotPricingForBookings, setPilotPricingForBookings] = useState([]);
  const [pricingImages, setPricingImages] = useState([]);
  const [pricingImageUploading, setPricingImageUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Szűrők és sorbarendezés (foglalásokhoz)
  const [filterDate, setFilterDate] = useState('');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState('');
  const [filterPaymentMethod, setFilterPaymentMethod] = useState('');
  const [filterCustomerName, setFilterCustomerName] = useState('');
  const [sortBy, setSortBy] = useState('created_at'); // 'created_at', 'date', 'name', 'amount'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'
  
  // Szűrő elérhetőségekhez
  const [filterInstructorName, setFilterInstructorName] = useState('');
  
  // Elérhetőség form
  const [showAvailabilityForm, setShowAvailabilityForm] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedHours, setSelectedHours] = useState([]);
  const [availabilitySubTab, setAvailabilitySubTab] = useState('calendar'); // 'calendar' | 'list'
  const [availabilityCalendarWeekFrom, setAvailabilityCalendarWeekFrom] = useState(() => {
    const d = new Date();
    const monday = new Date(d);
    monday.setDate(d.getDate() - d.getDay() + (d.getDay() === 0 ? -6 : 1));
    return monday.toISOString().slice(0, 10);
  });
  const [availabilityCalendarData, setAvailabilityCalendarData] = useState({ days: [] });
  const [availabilityFlightType, setAvailabilityFlightType] = useState('both'); // 'city' | 'city_to_city' | 'both'
  const [availabilityDestination, setAvailabilityDestination] = useState(''); // Budapest -> X (city-to-city only)
  const [availabilityAircraftId, setAvailabilityAircraftId] = useState('');
  
  // Oktató form
  const [showInstructorForm, setShowInstructorForm] = useState(false);
  const [editingInstructor, setEditingInstructor] = useState(null);
  const [instructorForm, setInstructorForm] = useState({
    username: '',
    password: '',
    name: '',
    email: '',
    phone: '',
    notes: '',
    status: 'approved',
    can_view_dashboard: false,
    aircraft_type: '',
    aircraft_registration: '',
    aircraft_types: '',
    total_flight_hours: '',
    recent_flight_hours: '',
    hours_on_type: '',
    licence_qualification: '',
    ratings: '',
    license_valid_until: '',
    medical_valid_until: '',
    pax_certificate_date: '',
    partner_campaign_id: ''
  });
  const [showPartnerCampaignForm, setShowPartnerCampaignForm] = useState(false);
  const [editingPartnerCampaign, setEditingPartnerCampaign] = useState(null);
  const [partnerCampaignForm, setPartnerCampaignForm] = useState({
    partner_key: '',
    name: '',
    landing_path: '',
    coupon: '',
    is_active: true
  });
  
  // Ár form
  const [showPricingForm, setShowPricingForm] = useState(false);
  const [editingPricing, setEditingPricing] = useState(null);
  const [pricingForm, setPricingForm] = useState({
    name: '',
    description: '',
    amount: '',
    airport_tax_per_passenger: 10,
    pilot_markup_percent: '0',
    is_active: true,
    bookable: true,
    revolut_link: '',
    duration_minutes: 60,
    flight_type: 'city',
    destination: '',
    max_passengers: 3,
    max_total_weight_kg: 260,
    image_path: '',
    aircraft_id: '',
    offer_group_id: ''
  });

  const [vouchers, setVouchers] = useState([]);
  const [vouchersListSubTab, setVouchersListSubTab] = useState('active'); // 'active' | 'archive' – voucher igénylések listája
  const [voucherBookingsSubTab, setVoucherBookingsSubTab] = useState('active'); // 'active' | 'archive'
  const [voucherBookings, setVoucherBookings] = useState([]);

  const [flightRequests, setFlightRequests] = useState([]);
  const [flightRequestsFilterStatus, setFlightRequestsFilterStatus] = useState('');

  // Dashboard statisztikák
  const [dashboardDateFrom, setDashboardDateFrom] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [dashboardDateTo, setDashboardDateTo] = useState(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardCalendarMonth, setDashboardCalendarMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [dashboardFilterPilot, setDashboardFilterPilot] = useState('');
  const [dashboardFilterCustomer, setDashboardFilterCustomer] = useState('');
  const [dashboardFilterCustomerDebounced, setDashboardFilterCustomerDebounced] = useState('');
  const [dashboardExportingPdf, setDashboardExportingPdf] = useState(false);
  const [instructorFormMode, setInstructorFormMode] = useState('pilot');
  const [staffRole, setStaffRole] = useState('caa');
  const [profileChangesPendingCount, setProfileChangesPendingCount] = useState(0);
  const isPilotSelfService = userAccessRole === 'pilot' && !canManageAccesses;

  useEffect(() => {
    const timer = setTimeout(() => setDashboardFilterCustomerDebounced(dashboardFilterCustomer), 400);
    return () => clearTimeout(timer);
  }, [dashboardFilterCustomer]);

  useEffect(() => {
    loadData();
  }, [activeTab, bookingsSubTab, vouchersListSubTab, voucherBookingsSubTab, calendarWeekFrom, availabilitySubTab, availabilityCalendarWeekFrom, flightRequestsFilterStatus, canViewDashboard, t]);

  useEffect(() => {
    if (activeTab === 'dashboard' && canViewDashboard) {
      setDashboardLoading(true);
      const params = { from: dashboardDateFrom, to: dashboardDateTo };
      if (dashboardFilterPilot) params.instructor_id = dashboardFilterPilot;
      if (dashboardFilterCustomerDebounced.trim()) params.customer_name = dashboardFilterCustomerDebounced.trim();
      axios.get('/api/booking/dashboard-stats', { params })
        .then(res => {
          setDashboardData(res.data);
        })
        .catch(err => {
          setError(err.response?.data?.error || t('admin.messages.dashboardLoadErr'));
        })
        .finally(() => setDashboardLoading(false));
    }
  }, [activeTab, dashboardDateFrom, dashboardDateTo, dashboardFilterPilot, dashboardFilterCustomerDebounced, canViewDashboard, t]);

  useEffect(() => {
    if (isCaaUser && activeTab !== 'dashboard') {
      setActiveTab('dashboard');
    }
  }, [isCaaUser, activeTab]);

  const handleExportDashboardPdf = useCallback(async () => {
    setDashboardExportingPdf(true);
    setError('');
    try {
      const params = {
        from: dashboardDateFrom,
        to: dashboardDateTo,
        lang: (i18n.language || 'en').slice(0, 2)
      };
      if (dashboardFilterPilot) params.instructor_id = dashboardFilterPilot;
      if (dashboardFilterCustomerDebounced.trim()) {
        params.customer_name = dashboardFilterCustomerDebounced.trim();
      }
      const response = await axios.get('/api/booking/dashboard-report-pdf', {
        params,
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `oscair-dashboard-${dashboardDateFrom}_${dashboardDateTo}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      let message = t('admin.dashboard.exportPdfError');
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed.error) message = parsed.error;
        } catch {
          /* keep default message */
        }
      } else if (err.response?.data?.error) {
        message = err.response.data.error;
      }
      setError(message);
    } finally {
      setDashboardExportingPdf(false);
    }
  }, [
    dashboardDateFrom,
    dashboardDateTo,
    dashboardFilterPilot,
    dashboardFilterCustomerDebounced,
    i18n.language,
    t
  ]);

  // Ha az elérhetőség űrlap látható és van kiválasztott dátum, betöltjük a foglalt órákat (pl. form megnyitásakor már beállított dátummal)
  useEffect(() => {
    if (!showAvailabilityForm || !selectedDate) return;
    if (takenSlotsForDate.date === selectedDate) return;
    let cancelled = false;
    axios.get('/api/availability/taken-slots', { params: { date: selectedDate } })
      .then(res => {
        if (!cancelled) setTakenSlotsForDate({ date: selectedDate, details: res.data.details || [] });
      })
      .catch(err => {
        if (!cancelled) console.error('Taken slots load error:', err);
      });
    return () => { cancelled = true; };
  }, [showAvailabilityForm, selectedDate]);

  // Új foglalások ellenőrzése külön useEffect-ben
  useEffect(() => {
    // Új foglalások számának frissítése 10 másodpercenként
    const interval = setInterval(() => {
      if (activeTab === 'bookings' || activeTab === 'availability') {
        axios.get('/api/booking/new-bookings-count')
          .then(res => {
            const newCount = res.data.count || 0;
            // Ha az új foglalások száma nőtt, popup-ot jelenítünk meg
            setNewBookingsCount(prevCount => {
              if (newCount > prevCount && prevCount > 0) {
                setShowNewBookingPopup(true);
                // Popup automatikusan eltűnik 5 másodperc után
                setTimeout(() => {
                  setShowNewBookingPopup(false);
                }, 5000);
              }
              return newCount;
            });
          })
          .catch(err => console.error('Új foglalások száma betöltési hiba:', err));
      }
    }, 10000); // 10 másodperc
    
    return () => clearInterval(interval);
  }, [activeTab]);

  const loadData = async () => {
    try {
      if (activeTab === 'availability' || activeTab === 'bookings') {
        const promises = [
          axios.get('/api/availability/my-availability'),
          axios.get('/api/booking/my-bookings'),
          axios.get('/api/booking/my-bookings/cancelled'),
          axios.get('/api/booking/my-bookings', { params: { payment_method: 'voucher' } }),
          axios.get('/api/instructors'),
          axios.get('/api/booking/new-bookings-count'),
          axios.get('/api/owners/aircraft-options')
        ];
        const loadBookingCalendar = activeTab === 'bookings' && bookingsSubTab === 'calendar';
        const loadAvailabilityCalendar = activeTab === 'availability' && availabilitySubTab === 'calendar';
        if (loadBookingCalendar) {
          promises.push(axios.get('/api/booking/calendar-week', { params: { from: calendarWeekFrom } }));
        }
        if (loadAvailabilityCalendar) {
          promises.push(axios.get('/api/availability/calendar-week', { params: { from: availabilityCalendarWeekFrom } }));
        }
        const results = await Promise.all(promises);
        setAvailability(results[0].data.availability || []);
        setCurrentInstructorId(results[0].data.current_instructor_id ?? null);
        const normalBookings = results[1].data.bookings || [];
        const voucherBookings = results[3].data.bookings || [];
        const seen = new Set(normalBookings.map(b => b.id));
        const merged = [...normalBookings];
        voucherBookings.forEach(b => {
          if (!seen.has(b.id)) { seen.add(b.id); merged.push(b); }
        });
        merged.sort((a, b) => a.date !== b.date ? a.date.localeCompare(b.date) : a.hour - b.hour);
        setBookings(merged);
        setCancelledBookings(results[2].data.bookings);
        setInstructors(results[4].data.instructors);
        setNewBookingsCount(results[5].data.count || 0);
        setAircraftOptions(results[6].data.aircraft || []);
        if (loadBookingCalendar && results[7]) setCalendarData(results[7].data);
        if (loadAvailabilityCalendar) {
          const avCalIdx = loadBookingCalendar ? 8 : 7;
          if (results[avCalIdx]) setAvailabilityCalendarData(results[avCalIdx].data);
        }
        if (userAccessRole === 'pilot') {
          try {
            const pr = await axios.get('/api/pricing');
            setPilotPricingForBookings((pr.data.pricing || []).filter((p) => p.is_active));
          } catch {
            setPilotPricingForBookings([]);
          }
        } else {
          setPilotPricingForBookings([]);
        }
      } else if (ACCESS_TABS.has(activeTab)) {
        const requests = [axios.get('/api/instructors')];
        if (canManageAccesses) requests.push(axios.get('/api/owners/admin'));
        if (canManageAccesses) requests.push(axios.get('/api/owners/admin/aircraft-options'));
        if (canManageAccesses) requests.push(axios.get('/api/partners/admin/campaigns'));
        if (canManageAccesses) requests.push(axios.get('/api/partners/admin/accounts'));
        const [instructorsRes, ownersRes, aircraftRes, campaignsRes, partnerAccountsRes] = await Promise.all(requests);
        setInstructors(instructorsRes.data.instructors || []);
        if (ownersRes) setOwnerAccounts(ownersRes.data.owners || []);
        if (aircraftRes) setAircraftOptions(aircraftRes.data.aircraft || []);
        if (campaignsRes) setPartnerCampaigns(campaignsRes.data.campaigns || []);
        if (partnerAccountsRes) setPartnerAccounts(partnerAccountsRes.data.accounts || []);
      } else if (activeTab === 'pricing') {
        const [pricingRes, imagesRes, aircraftRes] = await Promise.all([
          axios.get('/api/pricing'),
          axios.get('/api/upload/gallery-images'),
          axios.get('/api/owners/aircraft-options')
        ]);
        setPricing(pricingRes.data.pricing || []);
        setAircraftOptions(aircraftRes.data.aircraft || []);
        const fallbackImages = [
          { label: 'IMG_4327.JPEG', path: '/gallery/IMG_4327.JPEG' },
          { label: 'IMG_4406.JPEG', path: '/gallery/IMG_4406.JPEG' },
          { label: 'IMG_4700.JPEG', path: '/gallery/IMG_4700.JPEG' },
          { label: 'IMG_5095.JPEG', path: '/gallery/IMG_5095.JPEG' }
        ];
        const loadedImages = imagesRes.data?.images || [];
        setPricingImages(loadedImages.length > 0 ? loadedImages : fallbackImages);
      } else if (activeTab === 'flightRequests') {
        const params = {};
        if (flightRequestsFilterStatus) params.status = flightRequestsFilterStatus;
        const res = await axios.get('/api/flight-requests', { params });
        setFlightRequests(res.data.flightRequests || []);
      } else if (activeTab === 'vouchers') {
        const listArchived = vouchersListSubTab === 'archive' ? '1' : '0';
        const voucherScope = voucherBookingsSubTab === 'archive' ? 'archive' : 'active';
        const [voucherRes, voucherBookingsRes] = await Promise.all([
          axios.get('/api/voucher', { params: { archived: listArchived } }),
          axios.get('/api/booking/my-bookings', { params: { payment_method: 'voucher', voucher_scope: voucherScope } })
        ]);
        setVouchers(voucherRes.data.vouchers || []);
        setVoucherBookings(voucherBookingsRes.data.bookings || []);
      }
      // Pending voucherek száma a badge-hez (minden tabnál, csak nem archiváltak)
      if (activeTab !== 'vouchers') {
        try {
          const vRes = await axios.get('/api/voucher', { params: { archived: '0' } });
          setVouchers(vRes.data.vouchers || []);
        } catch (e) { /* ignore */ }
      }
      // Pending pilóták száma a badge-hez (canViewDashboard esetén)
      if (canViewDashboard && activeTab !== 'availability' && activeTab !== 'bookings' && !ACCESS_TABS.has(activeTab)) {
        try {
          const iRes = await axios.get('/api/instructors');
          setInstructors(iRes.data.instructors || []);
        } catch (e) { /* ignore */ }
      }
      if (canManageAccesses && !ACCESS_TABS.has(activeTab)) {
        try {
          const ownersRes = await axios.get('/api/owners/admin');
          setOwnerAccounts(ownersRes.data.owners || []);
        } catch (e) { /* ignore */ }
      }
      if (canManageAccesses) {
        try {
          const pcr = await axios.get('/api/pilot-profile/change-requests', { params: { status: 'pending' } });
          setProfileChangesPendingCount((pcr.data.requests || []).length);
        } catch (e) { /* ignore */ }
      }
    } catch (err) {
      const msg = err.response?.status === 401 || err.response?.status === 403
        ? t('admin.messages.accessDeniedReload')
        : err.response?.data?.error || err.message || t('admin.messages.dataLoadError');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Szűrt elérhetőségek
  const getFilteredAvailability = () => {
    let filtered = [...availability];
    
    // Szűrés oktató név szerint
    if (filterInstructorName) {
      filtered = filtered.filter(a => 
        a.instructor_name && a.instructor_name.toLowerCase().includes(filterInstructorName.toLowerCase())
      );
    }
    
    return filtered;
  };

  // Szűrt és rendezett foglalások
  const getFilteredAndSortedBookings = () => {
    let bookingsToShow;
    if (bookingsSubTab === 'cancelled') {
      bookingsToShow = cancelledBookings;
    } else {
      bookingsToShow = bookings;
    }
    let filtered = [...bookingsToShow];
    
    // Szűrés dátum szerint
    if (filterDate) {
      filtered = filtered.filter(b => b.date === filterDate);
    }
    
    // Szűrés fizetési státusz szerint
    if (filterPaymentStatus) {
      filtered = filtered.filter(b => b.payment_status === filterPaymentStatus);
    }
    
    // Szűrés fizetési mód szerint (voucher foglalások is megjelennek)
    if (filterPaymentMethod) {
      filtered = filtered.filter(b => b.payment_method === filterPaymentMethod);
    }
    
    // Szűrés név szerint
    if (filterCustomerName) {
      filtered = filtered.filter(b => 
        b.customer_name.toLowerCase().includes(filterCustomerName.toLowerCase())
      );
    }
    
    // Rendezés (alapértelmezett: legutóbbi felül)
    filtered.sort((a, b) => {
      let comparison = 0;
      
      if (sortBy === 'created_at') {
        const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
        comparison = timeA - timeB;
      } else if (sortBy === 'date') {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        comparison = dateA - dateB;
        if (comparison === 0) {
          comparison = a.hour - b.hour;
        }
      } else if (sortBy === 'name') {
        comparison = a.customer_name.localeCompare(b.customer_name);
      } else if (sortBy === 'amount') {
        comparison = (a.payment_amount || 0) - (b.payment_amount || 0);
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    
    return filtered;
  };

  const formatAircraftLabel = (aircraft) => {
    if (!aircraft) return '';
    return [aircraft.registration, aircraft.aircraft_type, aircraft.owner_name].filter(Boolean).join(' · ');
  };

  const getAircraftLabelById = (aircraftId, fallback = {}) => {
    const id = Number(aircraftId);
    const aircraft = aircraftOptions.find((item) => Number(item.id) === id);
    if (aircraft) return formatAircraftLabel(aircraft);
    return [fallback.aircraft_registration, fallback.aircraft_type].filter(Boolean).join(' · ');
  };

  // Elérhetőség kezelés
  const handleDateChange = async (e) => {
    const date = e.target.value;
    setSelectedDate(date);
    setSelectedHours([]);
    setTakenSlotsForDate({ date: '', details: [] });
    if (date) {
      try {
        const res = await axios.get('/api/availability/taken-slots', { params: { date } });
        setTakenSlotsForDate({ date, details: res.data.details || [] });
      } catch (err) {
        console.error('Taken slots load error:', err);
      }
    }
  };

  // A kiválasztott napon már foglalt órák (megjelennek szürkén – saját és másé is, hogy ne lehessen duplán megadni)
  const getHoursTakenByOthers = () => {
    if (!selectedDate) return [];
    if (takenSlotsForDate.date === selectedDate && takenSlotsForDate.details?.length) {
      return takenSlotsForDate.details.map(d => d.hour);
    }
    // Fallback: ha még nincs taken-slots válasz, a már betöltött elérhetőség listából (admin mindent lát)
    return availability
      .filter(a => a.date === selectedDate && (a.is_available === 1 || a.is_available === true || a.is_available === '1'))
      .map(a => a.hour);
  };

  const toggleHour = (hour) => {
    if (!canEditAvailability) return;
    if (getHoursTakenByOthers().includes(hour)) return;
    setSelectedHours(prev => 
      prev.includes(hour) 
        ? prev.filter(h => h !== hour)
        : [...prev, hour].sort((a, b) => a - b)
    );
  };

  const saveAvailability = async () => {
    if (!canEditAvailability) {
      setError(
        pilotCertBlockers.length
          ? pilotCertBlockers.join(' ')
          : t('admin.availability.errApprovedOnly')
      );
      return;
    }
    if (!selectedDate || selectedHours.length === 0) {
      setError(t('admin.messages.selectDateHours'));
      return;
    }
    if (!availabilityAircraftId) {
      setError(t('admin.aircraft.selectRequired', { defaultValue: 'Válassz repülőgépet az availability mentéséhez.' }));
      return;
    }

    try {
      const availabilityData = selectedHours.map(hour => ({
        date: selectedDate,
        hour: hour,
        is_available: true,
        flight_type: availabilityFlightType,
        destination: (availabilityFlightType === 'city_to_city' && availabilityDestination?.trim()) ? availabilityDestination.trim() : null,
        aircraft_id: availabilityAircraftId ? Number(availabilityAircraftId) : null
      }));

      const res = await axios.post('/api/availability/set-availability', {
        availability: availabilityData
      });

      setError('');
      if (res.data.successCount > 0) {
        setSuccess(
          res.data.errors?.length
            ? `${res.data.message} (${t('admin.messages.availabilitySlotsPartial')})`
            : res.data.message
        );
      } else if (res.data.errors?.length) {
        setError(res.data.errors.join(' '));
      } else {
        setSuccess(res.data.message || t('admin.messages.availabilitySaved'));
      }
      setShowAvailabilityForm(false);
      setSelectedDate('');
      setSelectedHours([]);
      setTakenSlotsForDate({ date: '', details: [] });
      loadData();
    } catch (err) {
      const base = err.response?.data?.error || t('admin.messages.saveErr');
      const extra = err.response?.data?.certIssues;
      setError(extra?.length ? `${base} ${extra.join(' ')}` : base);
    }
  };

  // Elérhetőség naptár: üres cella kattintás -> hozzáadás, saját cella -> törlés
  const handleAvailabilityCellClick = async (date, hour, slot) => {
    if (!canEditAvailability) {
      setError(
        pilotCertBlockers.length
          ? pilotCertBlockers.join(' ')
          : t('admin.availability.errApprovedOnly')
      );
      return;
    }
    if (slot.mine && slot.availability_id) {
      if (!window.confirm(t('admin.availability.confirmRemoveSlot'))) return;
      try {
        await axios.delete(`/api/availability/availability/${slot.availability_id}`);
        setSuccess(t('admin.messages.availabilityRemoved'));
        loadData();
      } catch (err) {
        const base = err.response?.data?.error || t('admin.messages.deleteErr');
        const extra = err.response?.data?.certIssues;
        setError(extra?.length ? `${base} ${extra.join(' ')}` : base);
      }
    } else if (slot.empty) {
      if (!availabilityAircraftId) {
        setError(t('admin.aircraft.selectRequired', { defaultValue: 'Válassz repülőgépet az availability mentéséhez.' }));
        return;
      }
      try {
        await axios.post('/api/availability/set-availability', {
          availability: [{
            date,
            hour,
            is_available: true,
            flight_type: availabilityFlightType,
            destination: (availabilityFlightType === 'city_to_city' && availabilityDestination?.trim()) ? availabilityDestination.trim() : null,
            aircraft_id: availabilityAircraftId ? Number(availabilityAircraftId) : null
          }]
        });
        setSuccess(t('admin.messages.availabilityAdded'));
        loadData();
      } catch (err) {
        const base = err.response?.data?.error || err.response?.data?.errors?.[0] || t('admin.messages.saveErr');
        const extra = err.response?.data?.certIssues;
        setError(extra?.length ? `${base} ${extra.join(' ')}` : base);
      }
    }
  };

  const deleteAvailability = async (id) => {
    if (!canEditAvailability) {
      setError(
        pilotCertBlockers.length
          ? pilotCertBlockers.join(' ')
          : t('admin.availability.errApprovedOnly')
      );
      return;
    }
    if (!window.confirm(t('admin.availability.confirmDeleteAvail'))) return;
    try {
      await axios.delete(`/api/availability/availability/${id}`);
      setSuccess(t('admin.messages.availabilityRemovedList'));
      loadData();
    } catch (err) {
      const base = err.response?.data?.error || t('admin.messages.deleteErr');
      const extra = err.response?.data?.certIssues;
      setError(extra?.length ? `${base} ${extra.join(' ')}` : base);
    }
  };

  // Oktató kezelés
  const openInstructorForm = (instructor = null, mode = 'pilot') => {
    setInstructorFormMode(mode);
    if (mode === 'admin' || mode === 'caa' || mode === 'partner') {
      if (instructor) {
        const r = getAccessRole(instructor);
        setStaffRole(r === 'admin' ? 'admin' : r === 'partner' ? 'partner' : 'caa');
      } else {
        setStaffRole(mode);
      }
    }
    if (instructor) {
      setEditingInstructor(instructor);
      setSelectedPilotAircraftIds([]);
      if (mode === 'pilot' && canManageAccesses) {
        axios.get(`/api/instructors/${instructor.id}/aircraft-assignments`)
          .then((res) => setSelectedPilotAircraftIds((res.data.aircraftIds || []).map(Number)))
          .catch(() => setSelectedPilotAircraftIds([]));
      }
      setInstructorForm({
        username: instructor.username,
        password: '',
        name: instructor.name,
        email: instructor.email,
        phone: instructor.phone || '',
        notes: instructor.notes || '',
        status: (instructor.status || 'approved').toLowerCase(),
        can_view_dashboard: Boolean(instructor.can_view_dashboard),
        aircraft_type: instructor.aircraft_type || '',
        aircraft_registration: instructor.aircraft_registration || '',
        aircraft_types: instructor.aircraft_types || '',
        total_flight_hours: instructor.total_flight_hours ?? '',
        recent_flight_hours: instructor.recent_flight_hours ?? '',
        hours_on_type: instructor.hours_on_type ?? '',
        licence_qualification: instructor.licence_qualification || '',
        ratings: instructor.ratings || '',
        license_valid_until: (instructor.license_valid_until || '').toString().slice(0, 10),
        medical_valid_until: (instructor.medical_valid_until || '').toString().slice(0, 10),
        pax_certificate_date: instructor.pax_certificate_date || '',
        partner_campaign_id: instructor.partner_campaign_id || ''
      });
    } else {
      setEditingInstructor(null);
      setSelectedPilotAircraftIds([]);
      setInstructorForm({
        username: '',
        password: '',
        name: '',
        email: '',
        phone: '',
        notes: '',
        status: 'approved',
        can_view_dashboard: mode === 'admin' || mode === 'caa',
        aircraft_type: '',
        aircraft_registration: '',
        aircraft_types: '',
        total_flight_hours: '',
        recent_flight_hours: '',
        hours_on_type: '',
        licence_qualification: '',
        ratings: '',
        license_valid_until: '',
        medical_valid_until: '',
        pax_certificate_date: '',
        partner_campaign_id: ''
      });
    }
    setShowInstructorForm(true);
  };

  const saveInstructor = async (e) => {
    e.preventDefault();
    try {
      const staffMode = instructorFormMode === 'admin' || instructorFormMode === 'caa' || instructorFormMode === 'partner';
      const accessRoleForSave = staffMode
        ? (instructorFormMode === 'admin' ? 'admin' : instructorFormMode === 'partner' ? 'partner' : 'caa')
        : 'pilot';
      if (accessRoleForSave === 'partner') {
        const payload = {
          username: instructorForm.username,
          password: instructorForm.password,
          name: instructorForm.name,
          email: instructorForm.email,
          phone: instructorForm.phone,
          notes: instructorForm.notes,
          partner_campaign_id: instructorForm.partner_campaign_id
        };
        if (editingInstructor) {
          await axios.put(`/api/partners/admin/accounts/${editingInstructor.id}`, payload);
        } else {
          await axios.post('/api/partners/admin/accounts', payload);
        }
        setSuccess(t('admin.partnerAccess.accountSaved'));
        setShowInstructorForm(false);
        setEditingInstructor(null);
        loadData();
        return;
      }
      if (editingInstructor) {
        const payload = {
          recent_flight_hours: instructorForm.recent_flight_hours || null,
          ratings: instructorForm.ratings || null,
          hours_on_type: instructorForm.hours_on_type || null,
          licence_qualification: instructorForm.licence_qualification || null,
          pilot_training_status: editingInstructor?.pilot_training_status || 'pending',
          ...instructorForm,
          status: staffMode ? 'approved' : instructorForm.status,
          access_role: accessRoleForSave,
          partner_campaign_id: accessRoleForSave === 'partner' ? instructorForm.partner_campaign_id : null,
          license_valid_until: instructorForm.license_valid_until?.trim() || null,
          medical_valid_until: instructorForm.medical_valid_until?.trim() || null
        };
        await axios.put(`/api/instructors/${editingInstructor.id}`, payload);
        if (!staffMode && canManageAccesses) {
          await axios.put(`/api/instructors/${editingInstructor.id}/aircraft-assignments`, {
            aircraft_ids: selectedPilotAircraftIds
          });
        }
        setSuccess(t('admin.messages.pilotUpdated'));
      } else {
        const created = await axios.post('/api/instructors', {
          ...instructorForm,
          status: staffMode ? 'approved' : instructorForm.status,
          access_role: accessRoleForSave,
          partner_campaign_id: accessRoleForSave === 'partner' ? instructorForm.partner_campaign_id : null,
          license_valid_until: instructorForm.license_valid_until?.trim() || null,
          medical_valid_until: instructorForm.medical_valid_until?.trim() || null
        });
        const newPilotId = created.data?.instructor?.id;
        if (!staffMode && canManageAccesses && newPilotId && selectedPilotAircraftIds.length > 0) {
          await axios.put(`/api/instructors/${newPilotId}/aircraft-assignments`, {
            aircraft_ids: selectedPilotAircraftIds
          });
        }
        setSuccess(t('admin.messages.pilotCreated'));
      }
      setShowInstructorForm(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || t('admin.instructors.saveErr'));
    }
  };

  const deleteInstructor = async (id) => {
    if (!window.confirm(t('admin.instructors.deleteConfirm'))) return;
    try {
      await axios.delete(`/api/instructors/${id}`);
      setSuccess(t('admin.messages.pilotDeleted'));
      loadData();
    } catch (err) {
      setError(t('admin.instructors.deleteErr'));
    }
  };

  const deletePartnerAccount = async (id) => {
    if (!window.confirm(t('admin.instructors.deleteConfirm'))) return;
    try {
      await axios.delete(`/api/partners/admin/accounts/${id}`);
      setSuccess(t('admin.partnerAccess.accountDeleted'));
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || t('admin.instructors.deleteErr'));
    }
  };

  const servePilotDocument = async (docPath) => {
    if (!docPath) return;
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(pilotDocumentFetchUrl(docPath), {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(t('admin.messages.docLoadErr'));
    }
  };

  const uploadAdminPilotDocumentFor = async (pilotId, documentType, file) => {
    if (!file || !pilotId) return;
    setError('');
    try {
      const fd = new FormData();
      fd.append(documentType, file);
      await axios.post(`/api/upload/admin/pilot-document/${pilotId}/${documentType}`, fd);
      setSuccess(t('admin.messages.docUploaded'));
      const res = await axios.get('/api/instructors');
      const list = res.data.instructors || [];
      setInstructors(list);
      const fresh = list.find((x) => Number(x.id) === Number(pilotId));
      if (fresh) setEditingInstructor(fresh);
    } catch (err) {
      setError(err.response?.data?.error || t('admin.messages.uploadFailed'));
    }
  };

  // Ár kezelés
  const openPricingForm = (price = null) => {
    if (price) {
      setEditingPricing(price);
      setPricingForm({
        name: price.name,
        description: price.description || '',
        amount: price.amount,
        airport_tax_per_passenger: price.airport_tax_per_passenger ?? 10,
        pilot_markup_percent: String(price.pilot_markup_percent != null ? Math.round(Number(price.pilot_markup_percent)) : 0),
        is_active: price.is_active,
        bookable: price.bookable !== 0 && price.bookable !== false,
        revolut_link: price.revolut_link || '',
        duration_minutes: price.duration_minutes ?? 60,
        flight_type: price.flight_type || 'city',
        destination: price.destination || '',
        max_passengers: price.max_passengers ?? 3,
        max_total_weight_kg: price.max_total_weight_kg ?? 260,
        image_path: price.image_path || '',
        aircraft_id: price.aircraft_id || '',
        offer_group_id: price.offer_group_id || price.id || ''
      });
    } else {
      setEditingPricing(null);
      setPricingForm({
        name: '',
        description: '',
        amount: '',
        airport_tax_per_passenger: 10,
        pilot_markup_percent: '0',
        is_active: true,
        bookable: true,
        revolut_link: '',
        duration_minutes: 60,
        flight_type: 'city',
        destination: '',
        max_passengers: 3,
        max_total_weight_kg: 260,
        image_path: '',
        aircraft_id: '',
        offer_group_id: ''
      });
    }
    setShowPricingForm(true);
  };

  const getPricingGroupId = (price) => Number(price.offer_group_id || price.id);

  const pricingGroupCounts = useMemo(() => {
    return pricing.reduce((acc, price) => {
      const groupId = getPricingGroupId(price);
      acc[groupId] = (acc[groupId] || 0) + 1;
      return acc;
    }, {});
  }, [pricing]);

  const uploadPricingImage = async (file) => {
    if (!file) return;
    setPricingImageUploading(true);
    setError('');
    try {
      const data = new FormData();
      data.append('image', file);
      const response = await axios.post('/api/upload/pricing-image', data);
      const path = response.data?.path;
      if (path) {
        setPricingForm((prev) => ({ ...prev, image_path: path }));
      }
      const imagesRes = await axios.get('/api/upload/gallery-images');
      setPricingImages(imagesRes.data?.images || []);
      setSuccess(t('admin.messages.priceImageOk'));
    } catch (err) {
      const responseError = err.response?.data?.error;
      const responseText = typeof err.response?.data === 'string' ? err.response.data : '';
      const status = err.response?.status;
      setError(
        responseError ||
        (responseText ? `${t('admin.messages.priceImageErr')} (${status || 'unknown'}): ${responseText}` : '') ||
        `${t('admin.messages.priceImageErr')}${status ? ` (HTTP ${status})` : ''}`
      );
    } finally {
      setPricingImageUploading(false);
    }
  };

  const savePricing = async (e) => {
    e.preventDefault();
    const payload = {
      ...pricingForm,
      airport_tax_per_passenger: Math.max(0, Number(pricingForm.airport_tax_per_passenger) || 0),
      pilot_markup_percent: Math.min(999, Math.max(0, parseInt(String(pricingForm.pilot_markup_percent || '0'), 10) || 0)),
      destination: pricingForm.flight_type === 'city_to_city' ? (pricingForm.destination?.trim() || null) : null,
      aircraft_id: pricingForm.aircraft_id ? Number(pricingForm.aircraft_id) : null,
      offer_group_id: pricingForm.offer_group_id ? Number(pricingForm.offer_group_id) : null
    };
    try {
      if (editingPricing) {
        await axios.put(`/api/pricing/${editingPricing.id}`, payload);
        setSuccess(t('admin.messages.priceUpdated'));
      } else {
        await axios.post('/api/pricing', payload);
        setSuccess(t('admin.messages.priceCreated'));
      }
      setShowPricingForm(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || t('admin.pricing.saveErr'));
    }
  };

  const createPricingVariant = async (price, targetDuration) => {
    const defaultAmount = price.amount ? String(Math.round(Number(price.amount))) : '';
    const amount = window.prompt(t('admin.pricing.variantAmountPrompt', { duration: targetDuration }), defaultAmount);
    if (amount === null) return;
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount < 0) {
      setError(t('admin.pricing.variantAmountErr'));
      return;
    }

    try {
      await axios.post(`/api/pricing/${price.id}/variant`, {
        amount: numericAmount,
        duration_minutes: targetDuration
      });
      setSuccess(t('admin.pricing.variantCreated'));
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || t('admin.pricing.saveErr'));
    }
  };

  const deletePricing = async (id) => {
    if (!window.confirm(t('admin.pricing.deleteConfirm'))) return;
    try {
      await axios.delete(`/api/pricing/${id}`);
      setSuccess(t('admin.messages.priceDeleted'));
      loadData();
    } catch (err) {
      setError(t('admin.instructors.deleteErr'));
    }
  };

  // Foglalás törlés
  const deleteBooking = async (id) => {
    if (!window.confirm(t('admin.messages.deleteBookingConfirm'))) return;
    try {
      await axios.delete(`/api/booking/booking/${id}`);
      setSuccess(t('admin.messages.bookingDeleted'));
      loadData();
    } catch (err) {
      setError(t('admin.instructors.deleteErr'));
    }
  };

  const handleArchiveVoucherBooking = async (id) => {
    try {
      await axios.put(`/api/booking/booking/${id}/archive`);
      setSuccess(t('admin.messages.bookingArchived'));
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || t('admin.messages.archiveErr'));
    }
  };

  const handleRestoreVoucherBooking = async (id) => {
    try {
      await axios.put(`/api/booking/booking/${id}/restore`);
      setSuccess(t('admin.messages.bookingRestored'));
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || t('admin.messages.restoreErr'));
    }
  };

  const handleArchiveVoucher = async (id) => {
    try {
      await axios.put(`/api/voucher/${id}/archive`);
      setSuccess(t('admin.messages.voucherArchived'));
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || t('admin.messages.archiveErr'));
    }
  };

  // Fizetési státusz frissítése
  const updatePaymentStatus = async (bookingId, paymentStatus, paymentInvoiceNumber, paymentDate) => {
    try {
      await axios.put(`/api/booking/booking/${bookingId}/payment`, {
        payment_status: paymentStatus,
        payment_invoice_number: paymentInvoiceNumber || null,
        payment_date: paymentDate || null
      });
      setSuccess(t('admin.messages.paymentUpdated'));
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || t('admin.messages.updateErr'));
    }
  };

  // Foglalás ellenőrzésének frissítése
  const updateBookingCheck = async (bookingId, checked) => {
    try {
      await axios.put(`/api/booking/booking/${bookingId}/check`, {
        checked: checked
      });
      loadData(); // Frissítjük az adatokat, hogy az új foglalások száma is frissüljön
    } catch (err) {
      setError(err.response?.data?.error || t('admin.messages.updateErr'));
    }
  };

  const formatDate = useCallback(
    (dateStr) => {
      const date = new Date(dateStr);
      return date.toLocaleDateString(dateLocale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    [dateLocale]
  );

  const formatHour = (hour) => {
    return `${hour.toString().padStart(2, '0')}:00`;
  };

  const getPaymentMethodLabel = useCallback(
    (method) => {
      const keyMap = {
        cash: 'admin.payment.cash',
        bank_transfer: 'admin.payment.bank_transfer',
        revolut: 'admin.payment.revolut',
        voucher: 'admin.payment.voucher'
      };
      if (method && keyMap[method]) return t(keyMap[method]);
      return method ? String(method) : t('admin.payment.notSpecified');
    },
    [t]
  );

  const navDropdownRef = useRef(null);
  const [navDropdownOpen, setNavDropdownOpen] = useState(false);

  useEffect(() => {
    if (!didMountScrollRef.current) {
      didMountScrollRef.current = true;
      return;
    }

    const scrollToAdminTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    };

    window.requestAnimationFrame(scrollToAdminTop);
    const settleScrollTimers = [80, 240, 500].map((delay) => window.setTimeout(scrollToAdminTop, delay));

    return () => {
      settleScrollTimers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [activeTab]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navDropdownRef.current && !navDropdownRef.current.contains(e.target)) {
        setNavDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getNavTabLabel = useCallback(
    (tab) => {
      const accessKeys = {
        accessAdmin: 'admin.nav.accessAdmin',
        accessPilots: 'admin.nav.accessPilots',
        myProfile: 'admin.nav.myProfile',
        accessCaa: 'admin.nav.accessCaa',
        accessOwners: 'admin.nav.accessOwners',
        accessPartners: 'admin.nav.accessPartners'
      };
      if (accessKeys[tab]) {
        return `${t('admin.nav.accesses')} · ${t(accessKeys[tab])}`;
      }
      const keys = {
        dashboard: 'admin.nav.dashboard',
        pilotApplications: 'admin.nav.pilotApplications',
        availability: 'admin.nav.availability',
        bookings: 'admin.nav.bookings',
        instructors: 'admin.nav.accessPilots',
        pricing: 'admin.nav.pricing',
        vouchers: 'admin.nav.vouchers',
        flightRequests: 'admin.nav.flightRequests',
        emailTemplates: 'admin.nav.emailTemplates'
      };
      return keys[tab] ? t(keys[tab]) : tab;
    },
    [t]
  );

  const adminRoleUsers = useMemo(
    () => instructors.filter((i) => getAccessRole(i) === 'admin'),
    [instructors]
  );
  const caaRoleUsers = useMemo(
    () => instructors.filter((i) => getAccessRole(i) === 'caa'),
    [instructors]
  );
  const partnerRoleUsers = useMemo(
    () => partnerAccounts,
    [partnerAccounts]
  );
  const operationalPilots = useMemo(
    () => instructors.filter((i) => getAccessRole(i) === 'pilot'),
    [instructors]
  );
  const caaApprovedPilots = operationalPilots;

  const pilotApplicationsQueue = instructors.filter((i) => {
    const s = (i.status || '').toLowerCase();
    return (s === 'pending' || s === 'rejected') && !getAccessRole(i);
  });

  const ownerApplicationsQueue = ownerAccounts.filter((owner) => {
    const s = (owner.status || owner.owner_status || 'pending').toLowerCase();
    return s === 'pending' || s === 'rejected';
  });

  const approveApplication = async (pilotId, accessRole) => {
    await axios.put(`/api/instructors/${pilotId}/approve`, { access_role: accessRole });
    setSuccess(t('admin.messages.pilotApproved'));
    loadData();
  };

  const approveOwnerAccount = async (ownerId) => {
    await axios.put(`/api/owners/admin/${ownerId}/approve`);
    setSuccess(t('admin.messages.ownerApproved'));
    loadData();
  };

  const rejectOwnerAccount = async (ownerId) => {
    const reason = window.prompt(t('admin.ownerAccounts.rejectPrompt'));
    await axios.put(`/api/owners/admin/${ownerId}/reject`, { reason: reason || '' });
    setSuccess(t('admin.messages.ownerRejected'));
    loadData();
  };

  const openPartnerCampaignForm = (campaign = null) => {
    setEditingPartnerCampaign(campaign);
    setPartnerCampaignForm({
      partner_key: campaign?.partner_key || '',
      name: campaign?.name || '',
      landing_path: campaign?.landing_path || '',
      coupon: campaign?.coupon || '',
      is_active: campaign ? campaign.is_active !== 0 && campaign.is_active !== false : true
    });
    setShowPartnerCampaignForm(true);
  };

  const savePartnerCampaign = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...partnerCampaignForm,
        partner_key: partnerCampaignForm.partner_key.trim().toLowerCase(),
        name: partnerCampaignForm.name.trim(),
        landing_path: partnerCampaignForm.landing_path.trim(),
        coupon: partnerCampaignForm.coupon.trim().toUpperCase() || null
      };
      if (editingPartnerCampaign) {
        await axios.put(`/api/partners/admin/campaigns/${editingPartnerCampaign.id}`, payload);
      } else {
        await axios.post('/api/partners/admin/campaigns', payload);
      }
      setShowPartnerCampaignForm(false);
      setEditingPartnerCampaign(null);
      setSuccess(t('admin.partnerAccess.campaignSaved'));
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || t('admin.partnerAccess.campaignSaveError'));
    }
  };

  const renderStaffUserForm = (expectedMode) => {
    if (!showInstructorForm || instructorFormMode !== expectedMode) return null;
    const isAdminForm = expectedMode === 'admin';
    const isPartnerForm = expectedMode === 'partner';
    const formTitle = editingInstructor
      ? t('admin.instructors.editTitle')
      : (isAdminForm ? t('admin.accessAdmin.addNew') : isPartnerForm ? t('admin.partnerAccess.addUser') : t('admin.accessCaa.addNew'));
    return (
      <div className="card">
        <h3>{formTitle}</h3>
        <form onSubmit={saveInstructor}>
          <div className="form-group">
            <label>{t('admin.instructors.username')}</label>
            <input
              type="text"
              value={instructorForm.username}
              onChange={(e) => setInstructorForm({ ...instructorForm, username: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>
              {t('admin.instructors.password')}
              {editingInstructor ? ` ${t('admin.instructors.passwordHintEdit')}` : ` ${t('admin.instructors.passwordRequired')}`}
            </label>
            <input
              type="password"
              value={instructorForm.password}
              onChange={(e) => setInstructorForm({ ...instructorForm, password: e.target.value })}
              required={!editingInstructor}
            />
          </div>
          <div className="form-group">
            <label>{t('admin.instructors.name')}</label>
            <input
              type="text"
              value={instructorForm.name}
              onChange={(e) => setInstructorForm({ ...instructorForm, name: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('admin.instructors.email')}</label>
            <input
              type="email"
              value={instructorForm.email}
              onChange={(e) => setInstructorForm({ ...instructorForm, email: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>{t('admin.instructors.phone')}</label>
            <input
              type="tel"
              value={instructorForm.phone}
              onChange={(e) => setInstructorForm({ ...instructorForm, phone: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>{t('admin.instructors.notes')}</label>
            <textarea
              value={instructorForm.notes}
              onChange={(e) => setInstructorForm({ ...instructorForm, notes: e.target.value })}
              rows="3"
            />
          </div>
          {isPartnerForm && (
            <div className="form-group">
              <label>{t('admin.partnerAccess.assignedCampaign')}</label>
              <select
                value={instructorForm.partner_campaign_id}
                onChange={(e) => setInstructorForm({ ...instructorForm, partner_campaign_id: e.target.value })}
                required
              >
                <option value="">{t('admin.partnerAccess.selectCampaign')}</option>
                {partnerCampaigns.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name} ({campaign.landing_path})
                  </option>
                ))}
              </select>
            </div>
          )}
          {editingInstructor && editingInstructor.isAdminAccount ? (
            <p className="form-hint">{t('admin.accessAdmin.systemAdminLocked')}</p>
          ) : (
            <p className="form-hint">
              {isAdminForm
                ? t('admin.accessAdmin.creatingAsAdmin')
                : isPartnerForm
                  ? t('admin.partnerAccess.creatingAsPartner')
                  : t('admin.accessCaa.creatingAsCaa')}
            </p>
          )}
          <div className="form-actions">
            <button type="submit" className="btn btn-success">
              {editingInstructor ? t('admin.instructors.update') : t('admin.instructors.create')}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setShowInstructorForm(false)}>
              {t('admin.instructors.cancel')}
            </button>
          </div>
        </form>
      </div>
    );
  };

  const renderStaffUserListItem = (acc, listMode) => (
    <div key={acc.id} className="instructor-item">
      <div className="instructor-info">
        <strong>{acc.name}</strong>
        <p>{t('admin.instructors.emailLbl')} {acc.email}</p>
        <p>{t('admin.instructors.usernameLbl')} {acc.username}</p>
        {listMode === 'partner' && (
          <p>
            {t('admin.partnerAccess.assignedCampaign')}{' '}
            {acc.campaign_name || partnerCampaigns.find((campaign) => Number(campaign.id) === Number(acc.partner_campaign_id))?.name || '-'}
          </p>
        )}
        {acc.phone && <p>{t('admin.instructors.phoneLbl')} {acc.phone}</p>}
      </div>
      <div className="instructor-actions">
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => openInstructorForm(acc, listMode)}
        >
          {t('admin.instructors.edit')}
        </button>
        {!acc.isAdminAccount && (
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={() => (listMode === 'partner' ? deletePartnerAccount(acc.id) : deleteInstructor(acc.id))}
          >
            {t('admin.instructors.delete')}
          </button>
        )}
      </div>
    </div>
  );

  const getPilotStatusLabel = useCallback(
    (status) => {
      const s = (status || '').toLowerCase();
      if (s === 'pending') return t('admin.pilotStatus.pending');
      if (s === 'approved') return t('admin.pilotStatus.approved');
      if (s === 'rejected') return t('admin.pilotStatus.rejected');
      return status || t('admin.pilotStatus.approved');
    },
    [t]
  );
  const getPaymentStatusLabel = useCallback(
    (status) => {
      const s = (status || '').toLowerCase();
      if (s === 'pending') return t('admin.payment.pending');
      if (s === 'paid') return t('admin.payment.paid');
      if (s === 'cancelled') return t('admin.payment.cancelled');
      return status || t('admin.payment.pending');
    },
    [t]
  );

  const getFlightRequestStatusLabel = useCallback(
    (status) => {
      const s = (status || '').toLowerCase();
      if (s === 'pending') return t('admin.flightRequests.statusPending');
      if (s === 'notified') return t('admin.flightRequests.statusNotified');
      if (s === 'cancelled') return t('admin.flightRequests.statusCancelled');
      return status || s;
    },
    [t]
  );

  const getVoucherStatusLabel = useCallback(
    (status) => {
      const s = (status || '').toLowerCase();
      if (s === 'pending') return t('admin.vouchers.statusPending');
      if (s === 'paid') return t('admin.vouchers.statusPaid');
      if (s === 'used') return t('admin.vouchers.statusUsed');
      if (s === 'archived') return t('admin.vouchers.statusArchived');
      return status || s;
    },
    [t]
  );

  if (loading) {
    return <div className="loading">{t('admin.loading')}</div>;
  }

  return (
    <div className="container admin-dashboard-page">
      <header className="admin-page-header">
        <div>
          <p className="admin-page-eyebrow">Oscair</p>
          <h1>{t('admin.title')}</h1>
        </div>
      </header>

      {!instructor?.isAdmin && pilotCertBlockers.length > 0 && (
        <div className="pilot-cert-global-alert" role="alert">
          <h2 className="pilot-cert-global-alert-title">{t('admin.certAlert.title')}</h2>
          <p className="pilot-cert-global-alert-lead">{t('admin.certAlert.lead')}</p>
          <ul className="pilot-cert-global-alert-list">
            {pilotCertBlockers.map((line, idx) => (
              <li key={idx}>{line}</li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Új foglalás popup */}
      {showNewBookingPopup && (
        <div className="new-booking-popup">
          <div className="popup-content">
            <div className="popup-icon">✈️</div>
            <div className="popup-text">
              <h3>{t('admin.newBookingPopup.title')}</h3>
              <p>{t('admin.newBookingPopup.body')}</p>
            </div>
            <button 
              className="popup-close"
              onClick={() => setShowNewBookingPopup(false)}
            >
              ×
            </button>
          </div>
        </div>
      )}
      
      {error && <div className="error">{error}</div>}
      {!canManageAccesses && userAccessRole === 'pilot' && (
        <PilotPrivateCommentsPanel />
      )}
      {success && <div className="success">{success}</div>}

      <div className="admin-nav" ref={navDropdownRef}>
        <button 
          className={`admin-nav-trigger ${navDropdownOpen ? 'open' : ''}`}
          onClick={() => setNavDropdownOpen(!navDropdownOpen)}
          aria-expanded={navDropdownOpen}
          aria-haspopup="true"
        >
          <span className="admin-nav-current">{getNavTabLabel(activeTab) || activeTab}</span>
          <span className="admin-nav-chevron">{navDropdownOpen ? '▲' : '▼'}</span>
        </button>
        <div className={`admin-nav-dropdown ${navDropdownOpen ? 'open' : ''}`}>
          <div className="admin-nav-dropdown-inner">
            {canViewDashboard && (
              <button
                className={activeTab === 'dashboard' ? 'active' : ''}
                onClick={() => { setActiveTab('dashboard'); setNavDropdownOpen(false); }}
              >
                <span>📊</span> {t('admin.nav.dashboard')}
              </button>
            )}
            {!isCaaUser && (
              <>
                <button
                  className={activeTab === 'availability' ? 'active' : ''}
                  onClick={() => { setActiveTab('availability'); setNavDropdownOpen(false); }}
                >
                  <span>📅</span> {t('admin.nav.availability')}
                </button>
                <button
                  className={activeTab === 'bookings' ? 'active' : ''}
                  onClick={() => { setActiveTab('bookings'); setNavDropdownOpen(false); }}
                >
                  <span>📋</span> {t('admin.nav.bookings')}
                  {newBookingsCount > 0 && <span className="admin-nav-badge">{newBookingsCount}</span>}
                </button>
              </>
            )}
            {canManageAccesses ? (
              <>
                <p className="admin-nav-group-label">{t('admin.nav.accesses')}</p>
                <button
                  className={`admin-nav-sub ${activeTab === 'accessAdmin' ? 'active' : ''}`}
                  onClick={() => { setActiveTab('accessAdmin'); setNavDropdownOpen(false); }}
                >
                  <span>🔐</span> {t('admin.nav.accessAdmin')}
                </button>
                <button
                  className={`admin-nav-sub ${activeTab === 'accessPilots' ? 'active' : ''}`}
                  onClick={() => { setActiveTab('accessPilots'); setNavDropdownOpen(false); }}
                >
                  <span>👤</span> {t('admin.nav.accessPilots')}
                  {profileChangesPendingCount > 0 && (
                    <span className="admin-nav-badge">{profileChangesPendingCount}</span>
                  )}
                </button>
                <button
                  className={`admin-nav-sub ${activeTab === 'accessCaa' ? 'active' : ''}`}
                  onClick={() => { setActiveTab('accessCaa'); setNavDropdownOpen(false); }}
                >
                  <span>🏛️</span> {t('admin.nav.accessCaa')}
                  {pilotApplicationsQueue.length > 0 && (
                    <span className="admin-nav-badge">{pilotApplicationsQueue.length}</span>
                  )}
                </button>
                <button
                  className={`admin-nav-sub ${activeTab === 'accessOwners' ? 'active' : ''}`}
                  onClick={() => { setActiveTab('accessOwners'); setNavDropdownOpen(false); }}
                >
                  <span>✈️</span> {t('admin.nav.accessOwners')}
                  {ownerApplicationsQueue.length > 0 && (
                    <span className="admin-nav-badge">{ownerApplicationsQueue.length}</span>
                  )}
                </button>
                <button
                  className={`admin-nav-sub ${activeTab === 'accessPartners' ? 'active' : ''}`}
                  onClick={() => { setActiveTab('accessPartners'); setNavDropdownOpen(false); }}
                >
                  <span>🤝</span> {t('admin.nav.accessPartners')}
                </button>
              </>
            ) : userAccessRole === 'pilot' ? (
              <button
                className={activeTab === 'accessPilots' ? 'active' : ''}
                onClick={() => { setActiveTab('accessPilots'); setNavDropdownOpen(false); }}
              >
                <span>👤</span> {t('admin.nav.myProfile')}
              </button>
            ) : null}
            {canManageAccesses && (
              <>
                <button
                  className={activeTab === 'emailTemplates' ? 'active' : ''}
                  onClick={() => { setActiveTab('emailTemplates'); setNavDropdownOpen(false); }}
                >
                  <span>✉️</span> {t('admin.nav.emailTemplates')}
                </button>
                <button
                  className={activeTab === 'pricing' ? 'active' : ''}
                  onClick={() => { setActiveTab('pricing'); setNavDropdownOpen(false); }}
                >
                  <span>💰</span> {t('admin.nav.pricing')}
                </button>
                <button
                  className={activeTab === 'vouchers' ? 'active' : ''}
                  onClick={() => { setActiveTab('vouchers'); setNavDropdownOpen(false); }}
                >
                  <span>🎫</span> {t('admin.nav.vouchers')}
                  {(vouchers.filter(v => v.status === 'pending').length) > 0 && (
                    <span className="admin-nav-badge">{vouchers.filter(v => v.status === 'pending').length}</span>
                  )}
                </button>
                <button
                  className={activeTab === 'flightRequests' ? 'active' : ''}
                  onClick={() => { setActiveTab('flightRequests'); setNavDropdownOpen(false); }}
                >
                  <span>✈️</span> {t('admin.nav.flightRequests')}
                </button>
              </>
            )}
            {canManageEmailTemplates && !canManageAccesses && (
              <button
                className={activeTab === 'emailTemplates' ? 'active' : ''}
                onClick={() => { setActiveTab('emailTemplates'); setNavDropdownOpen(false); }}
              >
                <span>✉️</span> {t('admin.nav.emailTemplates')}
              </button>
            )}
          </div>
        </div>
      </div>

      {activeTab === 'dashboard' && canViewDashboard && (
        <div className="dashboard-section dashboard-overview">
          <h2>{t('admin.dashboard.title')}</h2>
          <div className="dashboard-filters">
            <div className="dashboard-date-range">
              <label>{t('admin.dashboard.period')}</label>
              <input
                type="date"
                value={dashboardDateFrom}
                onChange={(e) => setDashboardDateFrom(e.target.value)}
              />
              <span>–</span>
              <input
                type="date"
                value={dashboardDateTo}
                onChange={(e) => setDashboardDateTo(e.target.value)}
              />
            </div>
            {instructor?.isAdmin && (
              <div className="dashboard-filter-group">
                <label>{t('admin.dashboard.filterPilot')}</label>
                <select
                  value={dashboardFilterPilot}
                  onChange={(e) => setDashboardFilterPilot(e.target.value)}
                >
                  <option value="">{t('admin.dashboard.filterPilotAll')}</option>
                  {instructors.map((inst) => (
                    <option key={inst.id} value={String(inst.id)}>
                      {inst.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="dashboard-filter-group">
              <label>{t('admin.dashboard.filterGuest')}</label>
              <input
                type="text"
                value={dashboardFilterCustomer}
                onChange={(e) => setDashboardFilterCustomer(e.target.value)}
                placeholder={t('admin.dashboard.filterGuestPh')}
              />
            </div>
            <div className="dashboard-filter-group dashboard-export-group">
              <button
                type="button"
                className="dashboard-export-btn"
                onClick={handleExportDashboardPdf}
                disabled={dashboardLoading || dashboardExportingPdf}
              >
                {dashboardExportingPdf ? t('admin.dashboard.exportPdfLoading') : t('admin.dashboard.exportPdf')}
              </button>
            </div>
          </div>
          {dashboardLoading ? (
            <div className="loading">{t('admin.loading')}</div>
          ) : dashboardData ? (
            <>
              {(() => {
                const pm = dashboardData.platformMetrics;
                if (!pm) {
                  return (
                    <p className="dashboard-platform-missing">{t('admin.dashboard.platformLoadMissing')}</p>
                  );
                }
                const formatCountPct = (item) => `${item?.count ?? 0} (${item?.percentage ?? 0}%)`;
                const expBucketLabel = (bucket) => {
                  const map = {
                    '0-99': 'admin.dashboard.expBucket0_99',
                    '100-299': 'admin.dashboard.expBucket100_299',
                    '300-599': 'admin.dashboard.expBucket300_599',
                    '600+': 'admin.dashboard.expBucket600plus'
                  };
                  return t(map[bucket] || bucket);
                };
                const licenceLabel = (label) => (
                  label === 'not_specified' ? t('admin.dashboard.licenceNotSpecified') : label
                );
                const routePieData = [
                  { name: t('admin.dashboard.routeAA'), value: pm.routes?.aa?.count || 0 },
                  { name: t('admin.dashboard.routeAB'), value: pm.routes?.ab?.count || 0 }
                ].filter((d) => d.value > 0);
                const experiencePieData = (pm.pilot_experience || []).map((row) => ({
                  name: expBucketLabel(row.bucket),
                  value: row.count
                }));
                const licencePieData = (pm.pilot_licence || []).map((row) => ({
                  name: licenceLabel(row.label),
                  value: row.count
                }));
                return (
                  <>
                    <h3 className="dashboard-subsection-title">{t('admin.dashboard.platformTitle')}</h3>
                    <p className="dashboard-platform-intro">{t('admin.dashboard.platformIntro')}</p>
                    <div className="dashboard-stats-grid dashboard-platform-grid">
                      <div className="dashboard-stat-card stat-platform">
                        <div className="stat-value">{pm.flights_count}</div>
                        <div className="stat-label">{t('admin.dashboard.statFlights')}</div>
                      </div>
                      <div className="dashboard-stat-card stat-platform" title={t('admin.dashboard.platformDurationHint')}>
                        <div className="stat-value">{pm.flight_duration_hours}</div>
                        <div className="stat-label">{t('admin.dashboard.statFlightHours')}</div>
                      </div>
                      <div className="dashboard-stat-card stat-platform">
                        <div className="stat-value">{pm.pilots_count}</div>
                        <div className="stat-label">{t('admin.dashboard.statPilots')}</div>
                        <div className="stat-sublabel">{t('admin.dashboard.statPilotsActiveHint', { count: pm.pilots_active_count ?? 0 })}</div>
                      </div>
                      <div className="dashboard-stat-card stat-platform">
                        <div className="stat-value">{pm.passengers_count}</div>
                        <div className="stat-label">{t('admin.dashboard.statPassengers')}</div>
                      </div>
                    </div>
                    <div className="dashboard-platform-routes">
                      <h4>{t('admin.dashboard.routeTitle')}</h4>
                      <ul className="dashboard-metric-pairs">
                        <li><span>{t('admin.dashboard.routeAA')}</span><strong>{formatCountPct(pm.routes?.aa)}</strong></li>
                        <li><span>{t('admin.dashboard.routeAB')}</span><strong>{formatCountPct(pm.routes?.ab)}</strong></li>
                        {(pm.routes?.uncategorised?.count ?? 0) > 0 && (
                          <li><span>{t('admin.dashboard.routeUncategorised')}</span><strong>{formatCountPct(pm.routes?.uncategorised)}</strong></li>
                        )}
                      </ul>
                    </div>
                    <div className="dashboard-charts-row dashboard-platform-charts">
                      <div className="dashboard-chart-card">
                        <h3>{t('admin.dashboard.routeChart')}</h3>
                        {routePieData.length > 0 ? (
                          <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                              <Pie data={routePieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                                {routePieData.map((_, i) => (
                                  <Cell key={i} fill={PLATFORM_CHART_COLORS[i % PLATFORM_CHART_COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip />
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                        ) : (
                          <p className="no-data">{t('admin.dashboard.noDataPeriod')}</p>
                        )}
                      </div>
                      <div className="dashboard-chart-card">
                        <h3>{t('admin.dashboard.pilotExperienceTitle')}</h3>
                        {experiencePieData.length > 0 ? (
                          <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                              <Pie data={experiencePieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                                {experiencePieData.map((_, i) => (
                                  <Cell key={i} fill={PLATFORM_CHART_COLORS[i % PLATFORM_CHART_COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip />
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                        ) : (
                          <p className="no-data">{t('admin.dashboard.noDataPeriod')}</p>
                        )}
                        {(pm.pilot_experience || []).length > 0 && (
                          <table className="dashboard-distribution-table">
                            <thead>
                              <tr>
                                <th>{t('admin.dashboard.pilotExperienceTitle')}</th>
                                <th>{t('admin.dashboard.distributionCount')}</th>
                                <th>{t('admin.dashboard.distributionShare')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pm.pilot_experience.map((row) => (
                                <tr key={row.bucket}>
                                  <td>{expBucketLabel(row.bucket)}</td>
                                  <td>{row.count}</td>
                                  <td>{row.percentage}%</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                      <div className="dashboard-chart-card">
                        <h3>{t('admin.dashboard.pilotLicenceTitle')}</h3>
                        {licencePieData.length > 0 ? (
                          <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                              <Pie data={licencePieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                                {licencePieData.map((_, i) => (
                                  <Cell key={i} fill={PLATFORM_CHART_COLORS[i % PLATFORM_CHART_COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip />
                              <Legend />
                            </PieChart>
                          </ResponsiveContainer>
                        ) : (
                          <p className="no-data">{t('admin.dashboard.noDataPeriod')}</p>
                        )}
                        {(pm.pilot_licence || []).length > 0 && (
                          <table className="dashboard-distribution-table">
                            <thead>
                              <tr>
                                <th>{t('admin.dashboard.pilotLicenceTitle')}</th>
                                <th>{t('admin.dashboard.distributionCount')}</th>
                                <th>{t('admin.dashboard.distributionShare')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pm.pilot_licence.map((row) => (
                                <tr key={row.label}>
                                  <td>{licenceLabel(row.label)}</td>
                                  <td>{row.count}</td>
                                  <td>{row.percentage}%</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>
                    <h4 className="dashboard-subsection-subtitle">{t('admin.dashboard.cancellationsTitle')}</h4>
                    <div className="dashboard-stats-grid dashboard-cancellation-grid">
                      <div className="dashboard-stat-card stat-cancelled-total">
                        <div className="stat-value">{pm.cancellations?.total ?? 0}</div>
                        <div className="stat-label">{t('admin.dashboard.cancelledTotal')}</div>
                      </div>
                      <div className="dashboard-stat-card stat-cancelled-weather">
                        <div className="stat-value">{pm.cancellations?.weather?.count ?? 0}</div>
                        <div className="stat-label">{t('admin.dashboard.cancelledWeather')}</div>
                        <div className="stat-sublabel">{formatCountPct(pm.cancellations?.weather)}</div>
                      </div>
                      <div className="dashboard-stat-card stat-cancelled-aircraft">
                        <div className="stat-value">{pm.cancellations?.aircraft_unavailable?.count ?? 0}</div>
                        <div className="stat-label">{t('admin.dashboard.cancelledAircraft')}</div>
                        <div className="stat-sublabel">{formatCountPct(pm.cancellations?.aircraft_unavailable)}</div>
                      </div>
                    </div>
                  </>
                );
              })()}
              <h3 className="dashboard-subsection-title dashboard-booking-summary-title">{t('admin.dashboard.bookingSummaryTitle')}</h3>
              <div className="dashboard-stats-grid">
                <div className="dashboard-stat-card">
                  <div className="stat-value">{dashboardData.stats.total}</div>
                  <div className="stat-label">{t('admin.dashboard.statTotal')}</div>
                </div>
                <div className="dashboard-stat-card stat-paid">
                  <div className="stat-value">{dashboardData.stats.paid}</div>
                  <div className="stat-label">{t('admin.dashboard.statPaid')}</div>
                </div>
                <div className="dashboard-stat-card stat-attended">
                  <div className="stat-value">{dashboardData.stats.attended}</div>
                  <div className="stat-label">{t('admin.dashboard.statAttended')}</div>
                </div>
                <div className="dashboard-stat-card stat-upcoming">
                  <div className="stat-value">{dashboardData.stats.upcoming}</div>
                  <div className="stat-label">{t('admin.dashboard.statUpcoming')}</div>
                </div>
                <div className="dashboard-stat-card stat-voucher">
                  <div className="stat-value">{dashboardData.stats.voucher_count || 0}</div>
                  <div className="stat-label">{t('admin.dashboard.statVoucher')}</div>
                </div>
                <div className="dashboard-stat-card stat-pending">
                  <div className="stat-value">{dashboardData.stats.pending_count ?? dashboardData.paymentBreakdown?.pending ?? 0}</div>
                  <div className="stat-label">{t('admin.dashboard.statPendingPay')}</div>
                </div>
              </div>
              <h3 className="dashboard-subsection-title">{t('admin.dashboard.financialTitle')}</h3>
              <div className="dashboard-stats-grid dashboard-financial-grid">
                <div className="dashboard-stat-card stat-financial-total">
                  <div className="stat-value">{Number(dashboardData.stats.total_amount || 0).toLocaleString(numberLocale)}</div>
                  <div className="stat-label">{t('admin.dashboard.euroTotalValue')}</div>
                </div>
                <div className="dashboard-stat-card stat-paid">
                  <div className="stat-value">{Number(dashboardData.stats.paid_amount || 0).toLocaleString(numberLocale)}</div>
                  <div className="stat-label">{t('admin.dashboard.euroPaid')}</div>
                </div>
                <div className="dashboard-stat-card stat-pending">
                  <div className="stat-value">{Number(dashboardData.stats.pending_amount || 0).toLocaleString(numberLocale)}</div>
                  <div className="stat-label">{t('admin.dashboard.euroPending')}</div>
                </div>
              </div>
              <div className="dashboard-charts-row">
                <div className="dashboard-chart-card dashboard-chart-card-wide">
                  <h3>{t('admin.dashboard.chartRevenue')}</h3>
                  {dashboardData.financialTrend?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={dashboardData.financialTrend.map(d => ({
                        ...d,
                        date: d.date.slice(5),
                        totalAmount: d.amount,
                        paidAmount: d.paid_amount,
                        pendingAmount: d.pending_amount
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                        <Tooltip formatter={(v) => `${Number(v).toLocaleString(numberLocale)} €`} />
                        <Line type="monotone" dataKey="totalAmount" stroke="#0077C8" name={t('admin.dashboard.legendTotal')} strokeWidth={2} />
                        <Line type="monotone" dataKey="paidAmount" stroke="#28a745" name={t('admin.dashboard.legendPaid')} strokeWidth={2} />
                        <Line type="monotone" dataKey="pendingAmount" stroke="#ffc107" name={t('admin.dashboard.legendPending')} strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="no-data">{t('admin.dashboard.noFinancialData')}</p>
                  )}
                </div>
                <div className="dashboard-chart-card">
                  <h3>{t('admin.dashboard.chartDailyBooking')}</h3>
                  {dashboardData.dailyTrend?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={dashboardData.dailyTrend.map(d => ({ ...d, date: d.date?.slice(5) || d.date }))}>
                        <XAxis dataKey="date" />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#0077C8" name={t('admin.dashboard.chartBooking')} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="no-data">{t('admin.dashboard.noDataPeriod')}</p>
                  )}
                </div>
                <div className="dashboard-chart-card">
                  <h3>{t('admin.dashboard.chartCreated')}</h3>
                  <p className="chart-hint">{t('admin.dashboard.chartCreatedHint')}</p>
                  {dashboardData.createdDailyTrend?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={dashboardData.createdDailyTrend.map(d => ({ ...d, date: d.date?.slice(5) || d.date }))}>
                        <XAxis dataKey="date" />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#17a2b8" name={t('admin.dashboard.chartBooking')} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="no-data">{t('admin.dashboard.noDataPeriod')}</p>
                  )}
                </div>
                <div className="dashboard-chart-card">
                  <h3>{t('admin.dashboard.chartPaymentCount')}</h3>
                  {Object.keys(dashboardData.paymentBreakdown || {}).length > 0 ? (
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={Object.entries(dashboardData.paymentBreakdown).map(([k, v]) => ({
                            name: k === 'paid' ? getPaymentStatusLabel('paid') : k === 'pending' ? getPaymentStatusLabel('pending') : k,
                            value: v
                          }))}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {Object.keys(dashboardData.paymentBreakdown || {}).map((_, i) => (
                            <Cell key={i} fill={['#28a745', '#ffc107', '#6c757d'][i % 3]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="no-data">{t('admin.dashboard.noData')}</p>
                  )}
                </div>
              </div>
              {(dashboardData.pendingBookings?.length > 0) && (
                <>
                  <h3 className="dashboard-subsection-title">{t('admin.dashboard.pendingBookingsTitle')}</h3>
                  <div className="dashboard-voucher-bookings-list dashboard-pending-bookings-list">
                    {dashboardData.pendingBookings.map(b => (
                      <div key={b.id} className="dashboard-voucher-booking-item">
                        <div className="voucher-booking-date">{formatDate(b.date)} – {String(b.hour).padStart(2, '0')}:00</div>
                        <div className="voucher-booking-customer">{b.customer_name}</div>
                        <div className="voucher-booking-email">{b.customer_email}</div>
                        {b.instructor_name && <span className="voucher-booking-instructor">{b.instructor_name}</span>}
                        {b.payment_amount && <span className="voucher-booking-amount">{parseInt(b.payment_amount).toLocaleString(numberLocale)} €</span>}
                        <span className="voucher-booking-status status-pending">{getPaymentStatusLabel('pending')}</span>
                      </div>
                    ))}
                  </div>
                  <p className="dashboard-voucher-hint">{t('admin.dashboard.pendingBookingsHint')}</p>
                </>
              )}
              {(dashboardData.voucherBookings?.length > 0) && (
                <>
                  <h3 className="dashboard-subsection-title">{t('admin.dashboard.voucherBookingsTitle')}</h3>
                  <div className="dashboard-voucher-bookings-list">
                    {dashboardData.voucherBookings.map(b => (
                      <div key={b.id} className="dashboard-voucher-booking-item">
                        <div className="voucher-booking-date">{formatDate(b.date)} – {String(b.hour).padStart(2, '0')}:00</div>
                        <div className="voucher-booking-customer">{b.customer_name}</div>
                        <div className="voucher-booking-email">{b.customer_email}</div>
                        {b.instructor_name && <span className="voucher-booking-instructor">{b.instructor_name}</span>}
                        <span className={`voucher-booking-status status-${b.payment_status || 'pending'}`}>
                          {b.payment_status === 'paid' ? getPaymentStatusLabel('paid') : getPaymentStatusLabel('pending')}
                        </span>
                        {b.archived && <span className="voucher-booking-archived">{t('admin.vouchers.statusArchived')}</span>}
                      </div>
                    ))}
                  </div>
                  <p className="dashboard-voucher-hint">{t('admin.dashboard.voucherBookingsHint')}</p>
                </>
              )}
              <div className="dashboard-calendar-hint">
                <p>{t('admin.dashboard.calendarHint')}</p>
              </div>
            </>
          ) : null}
        </div>
      )}

      {activeTab === 'accessAdmin' && canManageAccesses && (
        <div className="dashboard-section">
          <div className="section-header">
            <h2>{t('admin.accessAdmin.title')}</h2>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => openInstructorForm(null, 'admin')}
            >
              {t('admin.accessAdmin.addNew')}
            </button>
          </div>
          <p className="section-desc">{t('admin.accessAdmin.intro')}</p>

          {renderStaffUserForm('admin')}

          <div className="card">
            <h3>{t('admin.accessAdmin.listTitle')}</h3>
            {adminRoleUsers.length === 0 ? (
              <p>{t('admin.accessAdmin.empty')}</p>
            ) : (
              <div className="instructors-list">
                {adminRoleUsers.map((acc) => renderStaffUserListItem(acc, 'admin'))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'accessCaa' && canManageAccesses && (
        <div className="dashboard-section">
          <div className="section-header">
            <h2>{t('admin.nav.accessCaa')}</h2>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => openInstructorForm(null, 'caa')}
            >
              {t('admin.accessCaa.addNew')}
            </button>
          </div>
          <p className="section-desc">{t('admin.accessCaa.usersIntro')}</p>

          {renderStaffUserForm('caa')}

          <div className="card">
            <h3>{t('admin.accessCaa.listTitle')}</h3>
            {caaRoleUsers.length === 0 ? (
              <p>{t('admin.accessCaa.empty')}</p>
            ) : (
              <div className="instructors-list">
                {caaRoleUsers.map((acc) => renderStaffUserListItem(acc, 'caa'))}
              </div>
            )}
          </div>

          <h3 className="dashboard-subsection-title">{t('admin.pilotApplications.title')}</h3>
          <p className="section-desc">
            {t('admin.pilotApplications.intro')}
          </p>
          {pilotApplicationsQueue.length === 0 ? (
            <div className="card">
              <p>{t('admin.pilotApplications.empty')}</p>
            </div>
          ) : (
            <div className="card">
              <div className="pilot-applications-list">
                {pilotApplicationsQueue.map(p => (
                  <div
                    key={p.id}
                    className={`pilot-application-item${(p.status || '').toLowerCase() === 'rejected' ? ' pilot-application-rejected' : ''}`}
                  >
                    <div className="pilot-application-info">
                      <div className="pilot-application-name-row">
                        <strong>{p.name}</strong>
                        <span className={`pilot-app-status pilot-app-status-${(p.status || '').toLowerCase()}`}>
                          {getPilotStatusLabel(p.status)}
                        </span>
                      </div>
                      <p>{t('admin.pilotApplications.email')} {p.email}</p>
                      <p>{t('admin.pilotApplications.username')} {p.username}</p>
                      {p.phone && <p>{t('admin.pilotApplications.phone')} {p.phone}</p>}
                      <p>{t('admin.pilotApplications.registration')} {p.created_at ? new Date(p.created_at).toLocaleString(numberLocale) : '-'}</p>
                      {p.license_valid_until && (
                        <p><strong>{t('admin.pilotApplications.sepValid')}</strong> {p.license_valid_until}</p>
                      )}
                      {p.medical_valid_until && (
                        <p><strong>{t('admin.pilotApplications.medicalValid')}</strong> {p.medical_valid_until}</p>
                      )}
                      {(p.status || '').toLowerCase() === 'rejected' && p.rejection_reason && (
                        <p className="pilot-rejection-reason">
                          <strong>{t('admin.pilotApplications.rejectionReason')}</strong> {p.rejection_reason}
                        </p>
                      )}
                      <div className="pilot-document-links">
                        {p.license_document_path && (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem('token');
                                const res = await fetch(pilotDocumentFetchUrl(p.license_document_path), {
                                  headers: token ? { Authorization: `Bearer ${token}` } : {}
                                });
                                const blob = await res.blob();
                                const url = URL.createObjectURL(blob);
                                window.open(url, '_blank');
                              } catch (err) {
                                setError(t('admin.messages.docLoadErr'));
                              }
                            }}
                          >
                            {t('admin.pilotApplications.btnLicense')}
                          </button>
                        )}
                        {p.medical_document_path && (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem('token');
                                const res = await fetch(pilotDocumentFetchUrl(p.medical_document_path), {
                                  headers: token ? { Authorization: `Bearer ${token}` } : {}
                                });
                                const blob = await res.blob();
                                const url = URL.createObjectURL(blob);
                                window.open(url, '_blank');
                              } catch (err) {
                                setError(t('admin.messages.docLoadErr'));
                              }
                            }}
                          >
                            {t('admin.pilotApplications.btnMedical')}
                          </button>
                        )}
                        {p.logbook_last_page_path && (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem('token');
                                const res = await fetch(pilotDocumentFetchUrl(p.logbook_last_page_path), {
                                  headers: token ? { Authorization: `Bearer ${token}` } : {}
                                });
                                const blob = await res.blob();
                                const url = URL.createObjectURL(blob);
                                window.open(url, '_blank');
                              } catch (err) {
                                setError(t('admin.messages.docLoadErr'));
                              }
                            }}
                          >
                            {t('admin.pilotApplications.btnLogbook')}
                          </button>
                        )}
                        {(!p.license_document_path || !p.medical_document_path || !p.logbook_last_page_path) && (
                          <span className="pilot-docs-missing">{t('admin.pilotApplications.docsMissing')}</span>
                        )}
                      </div>
                    </div>
                    <div className="pilot-application-actions">
                      <button
                        className="btn btn-success btn-sm"
                        onClick={async () => {
                          try {
                            await approveApplication(p.id, 'pilot');
                          } catch (err) {
                            setError(err.response?.data?.error || t('admin.messages.genericErr'));
                          }
                        }}
                      >
                        {t('admin.pilotApplications.approveAsPilot')}
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={async () => {
                          try {
                            await approveApplication(p.id, 'caa');
                          } catch (err) {
                            setError(err.response?.data?.error || t('admin.messages.genericErr'));
                          }
                        }}
                      >
                        {t('admin.pilotApplications.approveAsCaa')}
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={async () => {
                          const reason = window.prompt(t('admin.pilotApplications.rejectPrompt'));
                          try {
                            await axios.put(`/api/instructors/${p.id}/reject`, { reason: reason || '' });
                            setSuccess(t('admin.messages.pilotRejected'));
                            loadData();
                          } catch (err) {
                            setError(err.response?.data?.error || t('admin.messages.genericErr'));
                          }
                        }}
                      >
                        {t('admin.pilotApplications.reject')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <h3 className="dashboard-subsection-title">{t('admin.accessCaa.overviewTitle')}</h3>
          <p className="section-desc">{t('admin.accessCaa.overviewIntro')}</p>
          <div className="card caa-overview-card">
            {caaApprovedPilots.length === 0 ? (
              <p>{t('admin.instructors.empty')}</p>
            ) : (
              <div className="caa-overview-table-wrap">
                <table className="caa-overview-table">
                  <thead>
                    <tr>
                      <th>{t('admin.accessCaa.colName')}</th>
                      <th>{t('admin.accessCaa.colStatus')}</th>
                      <th>{t('admin.accessCaa.colSep')}</th>
                      <th>{t('admin.accessCaa.colMedical')}</th>
                      <th>{t('admin.accessCaa.colDocs')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {caaApprovedPilots.map((p) => (
                      <tr key={p.id}>
                        <td>
                          <strong>{p.name}</strong>
                          <div className="caa-overview-sub">{p.email}</div>
                        </td>
                        <td>
                          <span className={`pilot-status-badge status-${(p.status || 'approved').toLowerCase()}`}>
                            {getPilotStatusLabel(p.status)}
                          </span>
                        </td>
                        <td>{p.license_valid_until ? String(p.license_valid_until).slice(0, 10) : t('admin.instructors.dash')}</td>
                        <td>{p.medical_valid_until ? String(p.medical_valid_until).slice(0, 10) : t('admin.instructors.dash')}</td>
                        <td className="caa-overview-docs">
                          {p.license_document_path && (
                            <button type="button" className="btn btn-secondary btn-sm" onClick={() => servePilotDocument(p.license_document_path)}>
                              {t('admin.pilotApplications.btnLicense')}
                            </button>
                          )}
                          {p.medical_document_path && (
                            <button type="button" className="btn btn-secondary btn-sm" onClick={() => servePilotDocument(p.medical_document_path)}>
                              {t('admin.pilotApplications.btnMedical')}
                            </button>
                          )}
                          {p.logbook_last_page_path && (
                            <button type="button" className="btn btn-secondary btn-sm" onClick={() => servePilotDocument(p.logbook_last_page_path)}>
                              {t('admin.pilotApplications.btnLogbook')}
                            </button>
                          )}
                          {!p.license_document_path && !p.medical_document_path && !p.logbook_last_page_path && (
                            <span className="pilot-docs-missing">{t('admin.pilotApplications.docsMissing')}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'accessPartners' && canManageAccesses && (
        <div className="dashboard-section">
          <div className="section-header">
            <h2>{t('admin.partnerAccess.title')}</h2>
            <button type="button" className="btn btn-primary" onClick={() => openPartnerCampaignForm(null)}>
              {t('admin.partnerAccess.addCampaign')}
            </button>
          </div>
          <p className="section-desc">{t('admin.partnerAccess.intro')}</p>

          {showPartnerCampaignForm && (
            <div className="card">
              <h3>{editingPartnerCampaign ? t('admin.partnerAccess.editCampaign') : t('admin.partnerAccess.addCampaign')}</h3>
              <form onSubmit={savePartnerCampaign}>
                <div className="form-group">
                  <label>{t('admin.partnerAccess.partnerKey')}</label>
                  <input
                    type="text"
                    value={partnerCampaignForm.partner_key}
                    onChange={(e) => setPartnerCampaignForm({ ...partnerCampaignForm, partner_key: e.target.value })}
                    required
                    placeholder="legenda"
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin.partnerAccess.campaignName')}</label>
                  <input
                    type="text"
                    value={partnerCampaignForm.name}
                    onChange={(e) => setPartnerCampaignForm({ ...partnerCampaignForm, name: e.target.value })}
                    required
                    placeholder="Legenda partner upsell"
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin.partnerAccess.landingPath')}</label>
                  <input
                    type="text"
                    value={partnerCampaignForm.landing_path}
                    onChange={(e) => setPartnerCampaignForm({ ...partnerCampaignForm, landing_path: e.target.value })}
                    required
                    placeholder="/legenda"
                  />
                  <small className="form-hint">{t('admin.partnerAccess.landingPathHelp')}</small>
                </div>
                <div className="form-group">
                  <label>{t('admin.partnerAccess.coupon')}</label>
                  <input
                    type="text"
                    value={partnerCampaignForm.coupon}
                    onChange={(e) => setPartnerCampaignForm({ ...partnerCampaignForm, coupon: e.target.value.toUpperCase() })}
                    placeholder="LEGENDA10"
                  />
                </div>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={partnerCampaignForm.is_active}
                    onChange={(e) => setPartnerCampaignForm({ ...partnerCampaignForm, is_active: e.target.checked })}
                  />
                  {t('admin.partnerAccess.active')}
                </label>
                <div className="form-actions">
                  <button type="submit" className="btn btn-success">{t('admin.partnerAccess.saveCampaign')}</button>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowPartnerCampaignForm(false)}>
                    {t('admin.instructors.cancel')}
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="card">
            <h3>{t('admin.partnerAccess.campaigns')}</h3>
            {partnerCampaigns.length === 0 ? (
              <p>{t('admin.partnerAccess.noCampaigns')}</p>
            ) : (
              <div className="instructors-list">
                {partnerCampaigns.map((campaign) => (
                  <div key={campaign.id} className="instructor-item">
                    <div className="instructor-info">
                      <strong>{campaign.name}</strong>
                      <p>{t('admin.partnerAccess.partnerKey')}: {campaign.partner_key}</p>
                      <p>{t('admin.partnerAccess.landingPath')}: {campaign.landing_path}</p>
                      <p>{t('admin.partnerAccess.coupon')}: {campaign.coupon || '-'}</p>
                      <p>{t('admin.partnerAccess.bookingsCount')}: {campaign.booking_count || 0}</p>
                    </div>
                    <div className="instructor-actions">
                      <button type="button" className="btn btn-primary btn-sm" onClick={() => openPartnerCampaignForm(campaign)}>
                        {t('admin.instructors.edit')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="section-header">
            <h3>{t('admin.partnerAccess.users')}</h3>
            <button type="button" className="btn btn-primary" onClick={() => openInstructorForm(null, 'partner')}>
              {t('admin.partnerAccess.addUser')}
            </button>
          </div>
          {renderStaffUserForm('partner')}
          <div className="card">
            {partnerRoleUsers.length === 0 ? (
              <p>{t('admin.partnerAccess.noUsers')}</p>
            ) : (
              <div className="instructors-list">
                {partnerRoleUsers.map((acc) => renderStaffUserListItem(acc, 'partner'))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'accessOwners' && canManageAccesses && (
        <div className="dashboard-section">
          <div className="section-header">
            <h2>{t('admin.nav.accessOwners')}</h2>
          </div>
          <p className="section-desc">{t('admin.ownerAccounts.intro')}</p>

          <div className="card">
            {ownerAccounts.length === 0 ? (
              <p>{t('admin.ownerAccounts.empty')}</p>
            ) : (
              <div className="pilot-applications-list">
                {ownerAccounts.map((owner) => {
                  const status = (owner.status || owner.owner_status || 'pending').toLowerCase();
                  const canModerate = status === 'pending' || status === 'rejected';
                  return (
                    <div
                      key={owner.id}
                      className={`pilot-application-item${status === 'rejected' ? ' pilot-application-rejected' : ''}`}
                    >
                      <div className="pilot-application-info">
                        <div className="pilot-application-name-row">
                          <strong>{owner.full_name || owner.name}</strong>
                          <span className={`pilot-app-status pilot-app-status-${status}`}>
                            {getPilotStatusLabel(status)}
                          </span>
                        </div>
                        <p>{t('admin.ownerAccounts.email')} {owner.email}</p>
                        <p>{t('admin.ownerAccounts.username')} {owner.username}</p>
                        {owner.phone && <p>{t('admin.ownerAccounts.phone')} {owner.phone}</p>}
                        <p>{t('admin.ownerAccounts.registrationDate')} {owner.created_at ? new Date(owner.created_at).toLocaleString(numberLocale) : '-'}</p>
                        <p><strong>{t('admin.ownerAccounts.aircraft')}</strong> {[owner.aircraft_type, owner.registration].filter(Boolean).join(' · ') || '-'}</p>
                        <p><strong>{t('admin.ownerAccounts.baseAirport')}</strong> {owner.base_airport || '-'}</p>
                        <p><strong>{t('admin.ownerAccounts.ownerStatus')}</strong> {getPilotStatusLabel(owner.owner_status || status)}</p>
                        <p><strong>{t('admin.ownerAccounts.aircraftStatus')}</strong> {getPilotStatusLabel(owner.aircraft_status || status)}</p>
                        {status === 'rejected' && owner.rejection_reason && (
                          <p className="pilot-rejection-reason">
                            <strong>{t('admin.pilotApplications.rejectionReason')}</strong> {owner.rejection_reason}
                          </p>
                        )}
                      </div>
                      <div className="pilot-application-actions">
                        {canModerate ? (
                          <>
                            <button
                              className="btn btn-success btn-sm"
                              onClick={async () => {
                                try {
                                  await approveOwnerAccount(owner.id);
                                } catch (err) {
                                  setError(err.response?.data?.error || t('admin.messages.genericErr'));
                                }
                              }}
                            >
                              {t('admin.ownerAccounts.approve')}
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={async () => {
                                try {
                                  await rejectOwnerAccount(owner.id);
                                } catch (err) {
                                  setError(err.response?.data?.error || t('admin.messages.genericErr'));
                                }
                              }}
                            >
                              {t('admin.ownerAccounts.reject')}
                            </button>
                          </>
                        ) : (
                          <span className="pilot-status-badge status-approved">{t('admin.ownerAccounts.approved')}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'availability' && (
        <div className="dashboard-section">
          <div className="section-header">
            <h2>{t('admin.availability.title')}</h2>
          </div>

          {!canEditAvailability && (
            <div className="card availability-approval-notice" role="status">
              {pilotStatusNorm !== 'approved' ? (
                <>
                  <p><strong>{t('admin.availability.readOnlyUntilApproved')}</strong></p>
                  {pilotStatusNorm === 'rejected' && instructor?.rejection_reason && (
                    <p className="availability-rejection-reminder">
                      <strong>{t('admin.availability.lastFeedback')}</strong> {instructor.rejection_reason}
                    </p>
                  )}
                  {pilotStatusNorm === 'pending' && (
                    <p>{t('admin.availability.pendingReview')}</p>
                  )}
                </>
              ) : (
                <>
                  <p>
                    <strong>
                      {t('admin.availability.certBlockedStrong')}
                    </strong>
                  </p>
                  <ul className="availability-cert-notice-list">
                    {pilotCertBlockers.map((line, idx) => (
                      <li key={idx}>{line}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}

          <div className="bookings-sub-tabs availability-sub-tabs">
            <button className={availabilitySubTab === 'calendar' ? 'active' : ''} onClick={() => setAvailabilitySubTab('calendar')}>{t('admin.availability.tabCalendar')}</button>
            <button className={availabilitySubTab === 'list' ? 'active' : ''} onClick={() => setAvailabilitySubTab('list')}>{t('admin.availability.tabList')}</button>
          </div>

          {availabilitySubTab === 'calendar' && (
            <div className="calendar-week-section availability-calendar-section">
              <p className="section-desc">{t('admin.availability.descCalendar')}</p>
              <div className="availability-flight-type-selector">
                <label>{t('admin.aircraft.label', { defaultValue: 'Repülőgép' })}</label>
                <select value={availabilityAircraftId} onChange={(e) => setAvailabilityAircraftId(e.target.value)} disabled={!canEditAvailability}>
                  <option value="">{t('admin.aircraft.select', { defaultValue: 'Válassz repülőgépet' })}</option>
                  {aircraftOptions.map((aircraft) => (
                    <option key={aircraft.id} value={aircraft.id}>{formatAircraftLabel(aircraft)}</option>
                  ))}
                </select>
                <label>{t('admin.availability.flightTypeNew')}</label>
                <select value={availabilityFlightType} onChange={(e) => setAvailabilityFlightType(e.target.value)} disabled={!canEditAvailability}>
                  <option value="both">{t('admin.availability.ftBoth')}</option>
                  <option value="city">{t('admin.availability.ftCity')}</option>
                  <option value="city_to_city">{t('admin.availability.ftC2c')}</option>
                </select>
                {availabilityFlightType === 'city_to_city' && (
                  <>
                    <label>{t('admin.availability.destination')}</label>
                    <input
                      type="text"
                      value={availabilityDestination}
                      onChange={(e) => setAvailabilityDestination(e.target.value)}
                      placeholder={t('admin.availability.destinationPh')}
                      className="availability-destination-input"
                      disabled={!canEditAvailability}
                    />
                  </>
                )}
              </div>
              <div className="calendar-week-nav">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => { const d = new Date(availabilityCalendarWeekFrom); d.setDate(d.getDate() - 7); setAvailabilityCalendarWeekFrom(d.toISOString().slice(0, 10)); }}>{t('admin.availability.prevWeek')}</button>
                <span className="calendar-week-range">
                  {availabilityCalendarData.days[0] && formatDate(availabilityCalendarData.days[0].date)} – {availabilityCalendarData.days[6] && formatDate(availabilityCalendarData.days[6].date)}
                </span>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => { const d = new Date(availabilityCalendarWeekFrom); d.setDate(d.getDate() + 7); setAvailabilityCalendarWeekFrom(d.toISOString().slice(0, 10)); }}>{t('admin.availability.nextWeek')}</button>
              </div>
              <div className="calendar-week-grid availability-calendar-grid">
                <div className="calendar-week-row header">
                  <div className="calendar-week-cell hour-label">{t('admin.availability.hour')}</div>
                  {availabilityCalendarData.days.map(day => (
                    <div key={day.date} className="calendar-week-cell day-header">{formatDate(day.date)}</div>
                  ))}
                </div>
                {[8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22].map(hour => (
                  <div key={hour} className="calendar-week-row">
                    <div className="calendar-week-cell hour-label">{formatHour(hour)}</div>
                    {availabilityCalendarData.days.map(day => {
                      const slot = day.hours.find(h => h.hour === hour) || { empty: true, mine: false };
                      const cellClass = slot.mine ? 'available' : slot.empty ? 'empty' : 'booked';
                      const isClickable = canEditAvailability && (slot.empty || slot.mine);
                      return (
                        <div
                          key={`${day.date}-${hour}`}
                          className={`calendar-week-cell slot ${cellClass} availability-slot ${isClickable ? 'clickable' : ''}`}
                          onClick={() => isClickable && handleAvailabilityCellClick(day.date, hour, slot)}
                          title={slot.empty ? t('admin.availability.titleAdd') : slot.mine ? t('admin.availability.titleRemove') : t('admin.availability.titleBooked', { name: slot.instructor_name || t('admin.availability.otherPilot') })}
                        >
                          {slot.mine ? '●' : slot.empty ? '' : '●'}
                          {!slot.empty && !slot.mine && <span className="availability-slot-instructor">{slot.instructor_name}</span>}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}

          {availabilitySubTab === 'list' && (
          <>
          <div className="availability-filters">
            <div className="filter-group">
              <label>{t('admin.availability.pilotFilter')}</label>
              <select
                value={filterInstructorName}
                onChange={(e) => setFilterInstructorName(e.target.value)}
              >
                <option value="">{t('admin.availability.allPilots')}</option>
                {instructors.map(instructor => (
                  <option key={instructor.id} value={instructor.name}>
                    {instructor.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="card">
            <div className="list-header-with-add">
              <h3>{t('admin.availability.savedTitle')}</h3>
              <button className="btn btn-primary btn-sm" onClick={() => setShowAvailabilityForm(!showAvailabilityForm)} disabled={!canEditAvailability}>
                {showAvailabilityForm ? t('admin.availability.cancel') : t('admin.availability.addNew')}
              </button>
            </div>
            {showAvailabilityForm && (
              <div className="availability-form-inline">
                <div className="form-group">
                  <label>{t('admin.aircraft.label', { defaultValue: 'Repülőgép' })}</label>
                  <select value={availabilityAircraftId} onChange={(e) => setAvailabilityAircraftId(e.target.value)} disabled={!canEditAvailability}>
                    <option value="">{t('admin.aircraft.select', { defaultValue: 'Válassz repülőgépet' })}</option>
                    {aircraftOptions.map((aircraft) => (
                      <option key={aircraft.id} value={aircraft.id}>{formatAircraftLabel(aircraft)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('admin.availability.date')}</label>
                  <input type="date" value={selectedDate} onChange={handleDateChange} min={new Date().toISOString().split('T')[0]} disabled={!canEditAvailability} />
                </div>
                <div className="form-group">
                  <label>{t('admin.availability.flightType')}</label>
                  <select value={availabilityFlightType} onChange={(e) => setAvailabilityFlightType(e.target.value)} disabled={!canEditAvailability}>
                    <option value="both">{t('admin.availability.ftBoth')}</option>
                    <option value="city">{t('admin.availability.ftCity')}</option>
                    <option value="city_to_city">{t('admin.availability.ftC2c')}</option>
                  </select>
                </div>
                {availabilityFlightType === 'city_to_city' && (
                  <div className="form-group">
                    <label>{t('admin.availability.destinationFromBp')}</label>
                    <input
                      type="text"
                      value={availabilityDestination}
                      onChange={(e) => setAvailabilityDestination(e.target.value)}
                      placeholder={t('admin.availability.destinationPhLong')}
                      disabled={!canEditAvailability}
                    />
                  </div>
                )}
                {selectedDate && (
                  <>
                    <div className="form-group">
                      <label>{t('admin.availability.hoursLabel')}</label>
                      <div className="hours-grid compact">
                        {Array.from({ length: 24 }, (_, i) => i).map(hour => {
                          const takenByOthers = getHoursTakenByOthers();
                          const isTaken = takenByOthers.includes(hour);
                          const detail = takenSlotsForDate.details?.find(d => d.hour === hour);
                          const instructorName = (isTaken && detail?.instructor_name) || t('admin.availability.other');
                          return (
                            <button key={hour} type="button" className={`hour-btn ${selectedHours.includes(hour) ? 'selected' : ''} ${isTaken ? 'hour-btn-taken' : ''}`} onClick={() => toggleHour(hour)} disabled={isTaken || !canEditAvailability} title={isTaken ? t('admin.availability.bookedName', { name: instructorName }) : ''}>{hour}</button>
                          );
                        })}
                      </div>
                    </div>
                    <button className="btn btn-success" onClick={saveAvailability} disabled={!canEditAvailability}>{t('admin.availability.save')}</button>
                  </>
                )}
              </div>
            )}
            {getFilteredAvailability().length === 0 ? (
              <p>{t('admin.availability.noAvailability')}</p>
            ) : (
              <div className="availability-list">
                {getFilteredAvailability().map(item => (
                  <div key={item.id} className="availability-item">
                    <div className="availability-info">
                      <span>{formatDate(item.date)} - {formatHour(item.hour)}</span>
                      {item.instructor_name && <span className="instructor-name-badge">{t('admin.availability.pilotPrefix')} {item.instructor_name}</span>}
                      {item.aircraft_id && (
                        <span className="availability-destination-badge">
                          {getAircraftLabelById(item.aircraft_id, item)}
                        </span>
                      )}
                      <span className={`availability-flight-type-badge flight-type-${item.flight_type || 'both'}`}>
                        {item.flight_type === 'city' ? t('admin.availability.flightShortCity') : item.flight_type === 'city_to_city' ? t('admin.availability.flightShortC2c') : t('admin.availability.flightShortBoth')}
                      </span>
                      {item.destination && (
                        <span className="availability-destination-badge">→ {item.destination}</span>
                      )}
                    </div>
                    <button className="btn btn-danger btn-sm" onClick={() => deleteAvailability(item.id)} disabled={!canEditAvailability}>{t('admin.availability.delete')}</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          </>
          )}
        </div>
      )}

      {activeTab === 'bookings' && (
        <div className="dashboard-section">
          <h2>{t('admin.bookings.title')}</h2>

          {userAccessRole === 'pilot' && pilotPricingForBookings.length > 0 && (
            <div className="card admin-pilot-pricing-ref">
              <h3 className="admin-pilot-pricing-ref-title">{t('admin.bookings.pilotPricingRef')}</h3>
              <p className="section-desc">{t('admin.bookings.pilotPricingRefHint')}</p>
              <ul className="admin-pilot-pricing-ref-list">
                {pilotPricingForBookings.map((p) => (
                  <li key={p.id}>
                    <strong>{p.name}</strong>
                    {' — '}
                    {parseInt(p.amount, 10).toLocaleString(numberLocale)} €
                    {formatPilotMarkupLabel(p.pilot_markup_percent) && (
                      <span className="pricing-pilot-markup">
                        {' '}
                        {formatPilotMarkupLabel(p.pilot_markup_percent)}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="bookings-sub-tabs">
            <button 
              className={bookingsSubTab === 'active' ? 'active' : ''}
              onClick={() => setBookingsSubTab('active')}
            >
              {t('admin.bookings.active')}
            </button>
            <button 
              className={bookingsSubTab === 'cancelled' ? 'active' : ''}
              onClick={() => setBookingsSubTab('cancelled')}
            >
              {t('admin.bookings.cancelled')}
            </button>
            <button 
              className={bookingsSubTab === 'calendar' ? 'active' : ''}
              onClick={() => setBookingsSubTab('calendar')}
            >
              {t('admin.bookings.calendar')}
            </button>
          </div>

          {bookingsSubTab === 'calendar' && (
            <div className="calendar-week-section">
              <div className="calendar-week-nav">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    const d = new Date(calendarWeekFrom);
                    d.setDate(d.getDate() - 7);
                    setCalendarWeekFrom(d.toISOString().slice(0, 10));
                  }}
                >
                  {t('admin.availability.prevWeek')}
                </button>
                <span className="calendar-week-range">
                  {calendarData.days[0] && formatDate(calendarData.days[0].date)} – {calendarData.days[6] && formatDate(calendarData.days[6].date)}
                </span>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    const d = new Date(calendarWeekFrom);
                    d.setDate(d.getDate() + 7);
                    setCalendarWeekFrom(d.toISOString().slice(0, 10));
                  }}
                >
                  {t('admin.availability.nextWeek')}
                </button>
              </div>
              <p className="section-desc">{t('admin.bookings.legendAvail')}</p>
              <div className="calendar-week-grid">
                <div className="calendar-week-row header">
                  <div className="calendar-week-cell hour-label">{t('admin.availability.hour')}</div>
                  {calendarData.days.map(day => (
                    <div key={day.date} className="calendar-week-cell day-header">
                      {formatDate(day.date)}
                    </div>
                  ))}
                </div>
                {[8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22].map(hour => (
                  <div key={hour} className="calendar-week-row">
                    <div className="calendar-week-cell hour-label">{formatHour(hour)}</div>
                    {calendarData.days.map(day => {
                      const slot = day.hours.find(h => h.hour === hour) || { available: false, booked: false, bookings: [] };
                      const cellClass = slot.booked ? 'booked' : slot.available ? 'available' : 'empty';
                      const tooltipContent = slot.bookings?.length > 0 ? slot.bookings.map((b, i) => (
                        <div key={i} className="calendar-slot-tooltip-booking">
                          <strong>{b.customer_name}</strong>
                          <span className="tooltip-operator">{t('admin.bookings.tooltipPilot')} {b.instructor_name}</span>
                          {b.customer_email && <span>{b.customer_email}</span>}
                          {b.customer_phone && <span>{t('admin.bookings.tooltipTel')} {b.customer_phone}</span>}
                          {b.notes && <span className="tooltip-notes">{b.notes}</span>}
                          {b.payment_method && <span>{t('admin.bookings.tooltipPayment')} {getPaymentMethodLabel(b.payment_method)}</span>}
                        </div>
                      )) : null;
                      return (
                        <div key={`${day.date}-${hour}`} className={`calendar-week-cell slot ${cellClass} calendar-slot-with-tooltip`} title={!slot.booked && slot.available ? t('admin.bookings.titleAvailable') : ''}>
                          {slot.booked ? '●' : slot.available ? '●' : ''}
                          {slot.booked && tooltipContent && (
                            <div className="calendar-slot-tooltip">
                              {tooltipContent}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}

          {bookingsSubTab !== 'calendar' && (
          <>
          <div className="bookings-filters">
            <div className="filter-row">
              <div className="filter-group">
                <label>{t('admin.bookings.dateFilter')}</label>
                <input
                  type="date"
                  value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                />
                {filterDate && (
                  <button 
                    className="btn btn-secondary btn-sm"
                    onClick={() => setFilterDate('')}
                    style={{ marginLeft: '5px' }}
                  >
                    ×
                  </button>
                )}
              </div>
              
              <div className="filter-group">
                <label>{t('admin.bookings.paymentStatus')}</label>
                <select
                  value={filterPaymentStatus}
                  onChange={(e) => setFilterPaymentStatus(e.target.value)}
                >
                  <option value="">{t('admin.bookings.filterAll')}</option>
                  <option value="pending">{t('admin.payment.pending')}</option>
                  <option value="paid">{t('admin.payment.paid')}</option>
                  <option value="cancelled">{t('admin.payment.cancelled')}</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label>{t('admin.bookings.paymentMethod')}</label>
                <select
                  value={filterPaymentMethod}
                  onChange={(e) => setFilterPaymentMethod(e.target.value)}
                >
                  <option value="">{t('admin.bookings.filterAllMethods')}</option>
                  <option value="bank_transfer">{t('admin.payment.bank_transfer')}</option>
                  <option value="revolut">{t('admin.payment.revolut')}</option>
                  <option value="cash">{t('admin.payment.cash')}</option>
                  <option value="voucher">{t('admin.payment.voucher')}</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label>{t('admin.bookings.customerName')}</label>
                <input
                  type="text"
                  value={filterCustomerName}
                  onChange={(e) => setFilterCustomerName(e.target.value)}
                  placeholder={t('admin.bookings.phCustomerSearch')}
                />
              </div>
            </div>
            
            <div className="sort-row">
              <div className="filter-group">
                <label>{t('admin.bookings.sortBy')}</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="created_at">{t('admin.bookings.sortCreated')}</option>
                  <option value="date">{t('admin.bookings.sortDate')}</option>
                  <option value="name">{t('admin.bookings.sortName')}</option>
                  <option value="amount">{t('admin.bookings.sortAmount')}</option>
                </select>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  style={{ marginLeft: '5px' }}
                >
                  <option value="asc">{t('admin.bookings.asc')}</option>
                  <option value="desc">{t('admin.bookings.desc')}</option>
                </select>
              </div>
            </div>
          </div>

          <div className="card">
            {getFilteredAndSortedBookings().length === 0 ? (
              <p>{t('admin.bookings.empty')}</p>
            ) : (
              <>
              <div className="bookings-list-header">
                  <div className="booking-row-col booking-col-index">{t('admin.bookings.colIndex')}</div>
                  <div className="booking-row-col booking-col-created">{t('admin.bookings.colCreated')}</div>
                  <div className="booking-row-col booking-col-datetime">{t('admin.bookings.colDateTime')}</div>
                  <div className="booking-row-col booking-col-customer">{t('admin.bookings.colGuest')}</div>
                  <div className="booking-row-col">{t('admin.bookings.colPilot')}</div>
                  <div className="booking-row-col">{t('admin.aircraft.label', { defaultValue: 'Repülőgép' })}</div>
                  <div className="booking-row-col">{t('admin.bookings.colMethod')}</div>
                  <div className="booking-row-col">{t('admin.bookings.colAmount')}</div>
                  <div className="booking-row-col">{t('admin.bookings.colStatus')}</div>
                  <div className="booking-row-col"></div>
                </div>
              <div className="bookings-list">
                {(() => {
                  const filteredBookings = getFilteredAndSortedBookings();
                  const sortedByAge = [...filteredBookings].sort((a, b) => {
                    const tA = a.created_at ? new Date(a.created_at).getTime() : 0;
                    const tB = b.created_at ? new Date(b.created_at).getTime() : 0;
                    return tA - tB;
                  });
                  const rowIndexByBookingId = new Map();
                  sortedByAge.forEach((b, i) => rowIndexByBookingId.set(b.id, i));
                  return filteredBookings.map((booking) => (
                  <BookingItem 
                    key={booking.id} 
                    booking={booking}
                    rowIndex={rowIndexByBookingId.get(booking.id) ?? 0}
                    formatDate={formatDate}
                    formatHour={formatHour}
                    getPaymentMethodLabel={getPaymentMethodLabel}
                    getPaymentStatusLabel={getPaymentStatusLabel}
                    updatePaymentStatus={updatePaymentStatus}
                    updateBookingCheck={updateBookingCheck}
                    deleteBooking={deleteBooking}
                    isCancelled={bookingsSubTab === 'cancelled'}
                    onArchive={undefined}
                    onRestore={undefined}
                  />
                  ));
                })()}
              </div>
              </>
            )}
          </div>
          </>
          )}
        </div>
      )}

      {activeTab === 'accessPilots' && (
        <div className="dashboard-section">
          {isPilotSelfService ? (
            <>
              <h2>{t('admin.nav.myProfile')}</h2>
              <PilotMyProfilePanel
                onSuccess={(msg) => { setSuccess(msg); setError(''); }}
                onError={(msg) => { setError(msg); setSuccess(''); }}
              />
            </>
          ) : (
          <>
          {canManageAccesses && (
            <PilotProfileChangesQueue
              onSuccess={(msg) => { setSuccess(msg); setError(''); loadData(); }}
              onError={(msg) => { setError(msg); setSuccess(''); }}
              onCountChange={setProfileChangesPendingCount}
            />
          )}
          <div className="section-header">
            <h2>{t('admin.instructors.title')}</h2>
            <button 
              className="btn btn-primary"
              onClick={() => openInstructorForm(null, 'pilot')}
            >
              {t('admin.instructors.addNew')}
            </button>
          </div>

          {showInstructorForm && instructorFormMode === 'pilot' && (
            <div className="card">
              <h3>{editingInstructor ? t('admin.instructors.editTitle') : t('admin.instructors.newTitle')}</h3>
              <form onSubmit={saveInstructor}>
                <div className="form-group">
                  <label>{t('admin.instructors.username')}</label>
                  <input
                    type="text"
                    value={instructorForm.username}
                    onChange={(e) => setInstructorForm({...instructorForm, username: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>
                    {t('admin.instructors.password')}
                    {editingInstructor ? ` ${t('admin.instructors.passwordHintEdit')}` : ` ${t('admin.instructors.passwordRequired')}`}
                  </label>
                  <input
                    type="password"
                    value={instructorForm.password}
                    onChange={(e) => setInstructorForm({...instructorForm, password: e.target.value})}
                    required={!editingInstructor}
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin.instructors.name')}</label>
                  <input
                    type="text"
                    value={instructorForm.name}
                    onChange={(e) => setInstructorForm({...instructorForm, name: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin.instructors.email')}</label>
                  <input
                    type="email"
                    value={instructorForm.email}
                    onChange={(e) => setInstructorForm({...instructorForm, email: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin.instructors.phone')}</label>
                  <input
                    type="tel"
                    value={instructorForm.phone}
                    onChange={(e) => setInstructorForm({...instructorForm, phone: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin.instructors.notes')}</label>
                  <textarea
                    value={instructorForm.notes}
                    onChange={(e) => setInstructorForm({...instructorForm, notes: e.target.value})}
                    rows="3"
                  />
                </div>
                {canViewDashboard && (
                  <div className="form-group">
                    <label>{t('admin.instructors.status')}</label>
                    <select
                      value={instructorForm.status}
                      onChange={(e) => setInstructorForm({...instructorForm, status: e.target.value})}
                    >
                      <option value="pending">{t('admin.instructors.optPending')}</option>
                      <option value="approved">{t('admin.instructors.optApproved')}</option>
                      <option value="rejected">{t('admin.instructors.optRejected')}</option>
                    </select>
                    <small style={{ color: '#666', display: 'block', marginTop: '4px' }}>{t('admin.instructors.statusHelp')}</small>
                  </div>
                )}
                <div className="form-group">
                  <label>{t('admin.instructors.aircraftType')}</label>
                  <input
                    type="text"
                    value={instructorForm.aircraft_type}
                    onChange={(e) => setInstructorForm({...instructorForm, aircraft_type: e.target.value})}
                    placeholder={t('admin.instructors.aircraftTypePh')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin.instructors.aircraftReg')}</label>
                  <input
                    type="text"
                    value={instructorForm.aircraft_registration}
                    onChange={(e) => setInstructorForm({...instructorForm, aircraft_registration: e.target.value})}
                    placeholder={t('admin.instructors.aircraftRegPh')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin.instructors.aircraftTypes')}</label>
                  <input
                    type="text"
                    value={instructorForm.aircraft_types}
                    onChange={(e) => setInstructorForm({...instructorForm, aircraft_types: e.target.value})}
                    placeholder={t('admin.instructors.aircraftTypesPh')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin.instructors.totalHours')}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={instructorForm.total_flight_hours}
                    onChange={(e) => setInstructorForm({...instructorForm, total_flight_hours: e.target.value})}
                    placeholder={t('admin.instructors.totalHoursPh')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin.instructors.sepValid')}</label>
                <div className="form-group">
                  <label>{t('admin.instructors.recentHours')}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={instructorForm.recent_flight_hours}
                    onChange={(e) => setInstructorForm({...instructorForm, recent_flight_hours: e.target.value})}
                    placeholder={t('admin.instructors.recentHoursPh')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin.instructors.hoursOnType')}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={instructorForm.hours_on_type}
                    onChange={(e) => setInstructorForm({...instructorForm, hours_on_type: e.target.value})}
                    placeholder={t('admin.instructors.hoursOnTypePh')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin.instructors.licenceQualification')}</label>
                  <input
                    type="text"
                    value={instructorForm.licence_qualification}
                    onChange={(e) => setInstructorForm({...instructorForm, licence_qualification: e.target.value})}
                    placeholder={t('admin.instructors.licenceQualificationPh')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin.instructors.ratingsPublic')}</label>
                  <input
                    type="text"
                    value={instructorForm.ratings}
                    onChange={(e) => setInstructorForm({...instructorForm, ratings: e.target.value})}
                    placeholder={t('admin.instructors.ratingsPublicPh')}
                  />
                </div>

                  <input
                    type="date"
                    value={instructorForm.license_valid_until}
                    onChange={(e) => setInstructorForm({ ...instructorForm, license_valid_until: e.target.value })}
                  />
                  <small style={{ color: '#666', display: 'block', marginTop: '4px' }}>
                    {t('admin.instructors.sepValidHelp')}
                  </small>
                </div>
                <div className="form-group">
                  <label>{t('admin.instructors.medicalValid')}</label>
                  <input
                    type="date"
                    value={instructorForm.medical_valid_until}
                    onChange={(e) => setInstructorForm({ ...instructorForm, medical_valid_until: e.target.value })}
                  />
                  <small style={{ color: '#666', display: 'block', marginTop: '4px' }}>
                    {t('admin.instructors.medicalValidHelp')}
                  </small>
                </div>
                <div className="form-group">
                  <label>{t('admin.instructors.paxCert')}</label>
                  <input
                    type="date"
                    value={instructorForm.pax_certificate_date}
                    onChange={(e) => setInstructorForm({...instructorForm, pax_certificate_date: e.target.value})}
                    placeholder={t('admin.instructors.datePh')}
                  />
                  <small style={{ color: '#666', display: 'block', marginTop: '4px' }}>{t('admin.instructors.paxCertHelp')}</small>
                </div>
                {editingInstructor && instructor?.isAdmin && (
                  <div className="form-group pilot-admin-documents">
                    <label>{t('admin.instructors.pilotDocs')}</label>
                    <small className="form-hint" style={{ display: 'block', marginBottom: '12px' }}>
                      {t('admin.instructors.pilotDocsHelp')}
                    </small>
                    <div className="pilot-admin-doc-row">
                      <span className="pilot-admin-doc-label">{t('admin.instructors.docLicense')}</span>
                      <div className="pilot-admin-doc-actions">
                        {editingInstructor.license_document_path && (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => servePilotDocument(editingInstructor.license_document_path)}
                          >
                            {t('admin.instructors.viewDownload')}
                          </button>
                        )}
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png,application/pdf"
                          className="pilot-admin-doc-file"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            e.target.value = '';
                            if (f) uploadAdminPilotDocumentFor(editingInstructor.id, 'license', f);
                          }}
                        />
                      </div>
                    </div>
                    <div className="pilot-admin-doc-row">
                      <span className="pilot-admin-doc-label">{t('admin.instructors.docMedical')}</span>
                      <div className="pilot-admin-doc-actions">
                        {editingInstructor.medical_document_path && (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => servePilotDocument(editingInstructor.medical_document_path)}
                          >
                            {t('admin.instructors.viewDownload')}
                          </button>
                        )}
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png,application/pdf"
                          className="pilot-admin-doc-file"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            e.target.value = '';
                            if (f) uploadAdminPilotDocumentFor(editingInstructor.id, 'medical', f);
                          }}
                        />
                      </div>
                    </div>
                    <div className="pilot-admin-doc-row">
                      <span className="pilot-admin-doc-label">{t('admin.instructors.docLogbook')}</span>
                      <div className="pilot-admin-doc-actions">
                        {editingInstructor.logbook_last_page_path && (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => servePilotDocument(editingInstructor.logbook_last_page_path)}
                          >
                            {t('admin.instructors.viewDownload')}
                          </button>
                        )}
                        <input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png,application/pdf"
                          className="pilot-admin-doc-file"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            e.target.value = '';
                            if (f) uploadAdminPilotDocumentFor(editingInstructor.id, 'logbook', f);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
                {canManageAccesses && instructorFormMode === 'pilot' && (
                  <div className="form-group pilot-admin-documents">
                    <label>{t('admin.aircraft.assignToPilot', { defaultValue: 'Repülőgép-hozzárendelés' })}</label>
                    <small className="form-hint" style={{ display: 'block', marginBottom: '12px' }}>
                      {t('admin.aircraft.assignHelp', { defaultValue: 'A pilóta csak az itt kiválasztott repülőgépekkel tud availabilityt megadni.' })}
                    </small>
                    {aircraftOptions.length === 0 ? (
                      <p className="section-desc">{t('admin.aircraft.noApproved', { defaultValue: 'Nincs jóváhagyott repülőgép.' })}</p>
                    ) : (
                      <div className="pilot-admin-doc-row" style={{ alignItems: 'flex-start' }}>
                        <span className="pilot-admin-doc-label">{t('admin.aircraft.allowedAircraft', { defaultValue: 'Engedélyezett gépek' })}</span>
                        <div className="pilot-admin-doc-actions" style={{ display: 'grid', gap: '8px' }}>
                          {aircraftOptions.map((aircraft) => {
                            const id = Number(aircraft.id);
                            return (
                              <label key={aircraft.id} className="checkbox-label" style={{ margin: 0 }}>
                                <input
                                  type="checkbox"
                                  checked={selectedPilotAircraftIds.includes(id)}
                                  onChange={(e) => {
                                    setSelectedPilotAircraftIds((prev) => (
                                      e.target.checked
                                        ? [...new Set([...prev, id])]
                                        : prev.filter((item) => item !== id)
                                    ));
                                  }}
                                />
                                <span>{formatAircraftLabel(aircraft)}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="form-actions">
                  <button type="submit" className="btn btn-success">
                    {editingInstructor ? t('admin.instructors.update') : t('admin.instructors.create')}
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => setShowInstructorForm(false)}
                  >
                    {t('admin.instructors.cancel')}
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="card">
            <h3>{t('admin.instructors.listTitle')}</h3>
            {operationalPilots.length === 0 ? (
              <p>{t('admin.instructors.empty')}</p>
            ) : (
              <div className="instructors-list">
                {operationalPilots.map(instructor => (
                  <div key={instructor.id} className="instructor-item">
                    <div className="instructor-info">
                      <strong>{instructor.name}</strong>
                      <p>{t('admin.instructors.emailLbl')} {instructor.email}</p>
                      <p>{t('admin.instructors.usernameLbl')} {instructor.username}</p>
                      <p>{t('admin.instructors.statusLbl')} <span className={`pilot-status-badge status-${(instructor.status || 'approved').toLowerCase()}`}>{getPilotStatusLabel(instructor.status)}</span></p>
                      {instructor.phone && <p>{t('admin.instructors.phoneLbl')} {instructor.phone}</p>}
                      {instructor.notes && <p>{t('admin.instructors.notesLbl')} {instructor.notes}</p>}
                      {(instructor.aircraft_type || instructor.aircraft_registration) && (
                        <p>{t('admin.instructors.aircraftLbl')} {[instructor.aircraft_type, instructor.aircraft_registration].filter(Boolean).join(' / ')}</p>
                      )}
                      {instructor.aircraft_types && <p>{t('admin.instructors.typesLbl')} {instructor.aircraft_types}</p>}
                      {instructor.total_flight_hours != null && instructor.total_flight_hours !== '' && <p>{t('admin.instructors.totalHoursLbl')} {Number(instructor.total_flight_hours).toLocaleString(numberLocale)} {t('admin.instructors.hoursSuffix')}</p>}
                      {instructor.recent_flight_hours != null && instructor.recent_flight_hours !== '' && (
                        <p>{t('admin.instructors.recentHoursLbl', { defaultValue: 'Last 12 months:' })} {Number(instructor.recent_flight_hours).toLocaleString(numberLocale)} {t('admin.instructors.hoursSuffix')}</p>
                      )}
                      {instructor.licence_qualification && <p>{t('admin.instructors.licenceQualification')}: {instructor.licence_qualification}</p>}
                      {instructor.ratings && <p>{t('admin.instructors.ratingsPublic')}: {instructor.ratings}</p>}
                      {instructor.hours_on_type != null && instructor.hours_on_type !== '' && (
                        <p>{t('admin.instructors.hoursOnType')}: {Number(instructor.hours_on_type).toLocaleString(numberLocale)} {t('admin.instructors.hoursSuffix')}</p>
                      )}
                      {(instructor.status || 'approved').toLowerCase() === 'approved' && (
                        <p>
                          <a href={`/pilot/${instructor.id}`} target="_blank" rel="noopener noreferrer">
                            {t('admin.instructors.publicProfileLink')}
                          </a>
                        </p>
                      )}
                      <p>
                        <strong>{t('admin.instructors.sepLbl')}</strong>{' '}
                        {instructor.license_valid_until
                          ? String(instructor.license_valid_until).slice(0, 10)
                          : t('admin.instructors.dash')}
                      </p>
                      <p>
                        <strong>{t('admin.instructors.medicalLbl')}</strong>{' '}
                        {instructor.medical_valid_until
                          ? String(instructor.medical_valid_until).slice(0, 10)
                          : t('admin.instructors.dash')}
                      </p>
                      {instructor.pax_certificate_date && <p>{t('admin.instructors.paxLbl')} {instructor.pax_certificate_date}</p>}
                    </div>
                    <div className="instructor-actions">
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => openInstructorForm(instructor, 'pilot')}
                      >
                        {t('admin.instructors.edit')}
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => deleteInstructor(instructor.id)}
                      >
                        {t('admin.instructors.delete')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          </>
          )}
        </div>
      )}

      {activeTab === 'pricing' && (
        <div className="dashboard-section">
          <div className="section-header">
            <h2>{t('admin.pricing.title')}</h2>
            <button 
              className="btn btn-primary"
              onClick={() => openPricingForm()}
            >
              {t('admin.pricing.addNew')}
            </button>
          </div>

          {showPricingForm && (
            <div className="card">
              <h3>{editingPricing ? t('admin.pricing.editTitle') : t('admin.pricing.newTitle')}</h3>
              <form onSubmit={savePricing}>
                <div className="form-group">
                  <label>{t('admin.pricing.name')}</label>
                  <input
                    type="text"
                    value={pricingForm.name}
                    onChange={(e) => setPricingForm({...pricingForm, name: e.target.value})}
                    required
                    placeholder={t('admin.pricing.namePh')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin.pricing.description')}</label>
                  <textarea
                    value={pricingForm.description}
                    onChange={(e) => setPricingForm({...pricingForm, description: e.target.value})}
                    rows="2"
                    placeholder={t('admin.pricing.descriptionPh')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin.pricing.amount')}</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={pricingForm.amount}
                    onChange={(e) => setPricingForm({...pricingForm, amount: e.target.value})}
                    required
                    placeholder={t('admin.pricing.amountPh')}
                  />
                  <small className="form-hint">{t('admin.pricing.amountHelp')}</small>
                </div>
                <div className="form-group">
                  <label>{t('admin.pricing.airportTax')}</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={pricingForm.airport_tax_per_passenger}
                    onChange={(e) => setPricingForm({...pricingForm, airport_tax_per_passenger: e.target.value})}
                    required
                    placeholder={t('admin.pricing.airportTaxPh')}
                  />
                  <small className="form-hint">{t('admin.pricing.airportTaxHelp')}</small>
                </div>
                <div className="form-group">
                  <label>{t('admin.aircraft.label', { defaultValue: 'Repülőgép' })}</label>
                  <select
                    value={pricingForm.aircraft_id || ''}
                    onChange={(e) => setPricingForm({...pricingForm, aircraft_id: e.target.value})}
                  >
                    <option value="">{t('admin.aircraft.globalPrice', { defaultValue: 'Globális ár, nincs konkrét gép' })}</option>
                    {aircraftOptions.map((aircraft) => (
                      <option key={aircraft.id} value={aircraft.id}>{formatAircraftLabel(aircraft)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('admin.pricing.pilotMarkup')}</label>
                  <input
                    type="number"
                    min="0"
                    max="999"
                    step="1"
                    value={pricingForm.pilot_markup_percent}
                    onChange={(e) => setPricingForm({...pricingForm, pilot_markup_percent: e.target.value})}
                    placeholder="0"
                  />
                  <small className="form-hint">{t('admin.pricing.pilotMarkupHelp')}</small>
                </div>
                <div className="form-group">
                  <label>{t('admin.pricing.maxPax')}</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={pricingForm.max_passengers}
                    onChange={(e) => setPricingForm({...pricingForm, max_passengers: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin.pricing.maxWeight')}</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={pricingForm.max_total_weight_kg}
                    onChange={(e) => setPricingForm({...pricingForm, max_total_weight_kg: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('admin.pricing.duration')}</label>
                  <select value={pricingForm.duration_minutes} onChange={(e) => setPricingForm({...pricingForm, duration_minutes: parseInt(e.target.value, 10)})}>
                    <option value={30}>{t('admin.pricing.dur30')}</option>
                    <option value={60}>{t('admin.pricing.dur60')}</option>
                    <option value={120}>{t('admin.pricing.dur120')}</option>
                    <option value={180}>{t('admin.pricing.dur180')}</option>
                    <option value={240}>{t('admin.pricing.dur240')}</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('admin.pricing.flightType')}</label>
                  <select value={pricingForm.flight_type} onChange={(e) => setPricingForm({...pricingForm, flight_type: e.target.value})}>
                    <option value="city">{t('admin.pricing.ftCity')}</option>
                    <option value="city_to_city">{t('admin.pricing.ftC2c')}</option>
                  </select>
                </div>
                {pricingForm.flight_type === 'city_to_city' && (
                  <div className="form-group">
                    <label>{t('admin.pricing.destination')}</label>
                    <input
                      type="text"
                      value={pricingForm.destination || ''}
                      onChange={(e) => setPricingForm({...pricingForm, destination: e.target.value})}
                      placeholder={t('admin.pricing.destinationPh')}
                    />
                  </div>
                )}
                <div className="form-group">
                  <label>{t('admin.pricing.revolut')}</label>
                  <input
                    type="url"
                    value={pricingForm.revolut_link || ''}
                    onChange={(e) => setPricingForm({...pricingForm, revolut_link: e.target.value})}
                    placeholder={t('admin.pricing.revolutPh')}
                  />
                  <small className="form-hint">{t('admin.pricing.revolutHelp')}</small>
                </div>
                <div className="form-group">
                  <label>{t('admin.pricing.offerImage')}</label>
                  <select
                    value={pricingForm.image_path || ''}
                    onChange={(e) => setPricingForm({...pricingForm, image_path: e.target.value})}
                  >
                    <option value="">{t('admin.pricing.noImage')}</option>
                    {pricingImages.map((image) => (
                      <option key={image.path} value={image.path}>{image.label}</option>
                    ))}
                  </select>
                  <small className="form-hint">{t('admin.pricing.imageHelp')}</small>
                </div>
                <div className="form-group">
                  <label>{t('admin.pricing.uploadImage')}</label>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp"
                    onChange={(e) => uploadPricingImage(e.target.files?.[0] || null)}
                    disabled={pricingImageUploading}
                  />
                  {pricingImageUploading && <small className="form-hint">{t('admin.pricing.uploading')}</small>}
                  {pricingForm.image_path && (
                    <div className="pricing-image-preview">
                      <img src={pricingForm.image_path} alt={t('admin.pricing.imageAlt')} />
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={pricingForm.is_active}
                      onChange={(e) => setPricingForm({...pricingForm, is_active: e.target.checked})}
                    />
                    {t('admin.pricing.activeHome')}
                  </label>
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={pricingForm.bookable}
                      onChange={(e) => setPricingForm({...pricingForm, bookable: e.target.checked})}
                    />
                    {t('admin.pricing.bookable')}
                  </label>
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-success">
                    {editingPricing ? t('admin.pricing.update') : t('admin.pricing.create')}
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => setShowPricingForm(false)}
                  >
                    {t('admin.pricing.cancel')}
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="card">
            <h3>{t('admin.pricing.listTitle')}</h3>
            {pricing.length === 0 ? (
              <p>{t('admin.pricing.empty')}</p>
            ) : (
              <div className="pricing-list">
                {pricing.map(price => (
                  <div key={price.id} className={`pricing-item ${!price.is_active ? 'inactive' : ''}`}>
                    <div className="pricing-info">
                      <strong>{price.name}</strong>
                      {price.description && <p>{price.description}</p>}
                      <p className="pricing-amount">
                        {parseInt(price.amount, 10).toLocaleString(numberLocale)} €
                        {formatPilotMarkupLabel(price.pilot_markup_percent) && (
                          <span className="pricing-pilot-markup"> {formatPilotMarkupLabel(price.pilot_markup_percent)}</span>
                        )}
                      </p>
                      <p className="pricing-capacity">
                        {t('admin.pricing.airportTaxList', { tax: Number(price.airport_tax_per_passenger ?? 10).toLocaleString(numberLocale) })}
                      </p>
                      <span className="pricing-duration-badge">{price.duration_minutes || 60} {t('admin.pricing.min')}</span>
                      {pricingGroupCounts[getPricingGroupId(price)] > 1 && (
                        <span className="pricing-variant-badge">
                          {t('admin.pricing.variantGroup', { count: pricingGroupCounts[getPricingGroupId(price)] })}
                        </span>
                      )}
                      <span className={`pricing-flight-type-badge flight-type-${price.flight_type || 'city'}`}>
                        {price.flight_type === 'city_to_city' ? t('admin.pricing.ftC2c') : t('admin.pricing.ftCity')}
                      </span>
                      {price.destination && (
                        <span className="pricing-destination-badge">→ {price.destination}</span>
                      )}
                      {price.aircraft_id && (
                        <span className="pricing-destination-badge">
                          {getAircraftLabelById(price.aircraft_id, price)}
                        </span>
                      )}
                      {price.revolut_link && (
                        <span className="pricing-revolut-badge" title={price.revolut_link}>{t('admin.pricing.revolutSet')}</span>
                      )}
                      {(price.max_passengers || price.max_total_weight_kg) && (
                        <p className="pricing-capacity">
                          {t('admin.pricing.capacity', { pax: price.max_passengers || 3, kg: Number(price.max_total_weight_kg || 260).toLocaleString(numberLocale) })}
                        </p>
                      )}
                      {price.image_path && (
                        <div className="pricing-card-thumb">
                          <img src={price.image_path} alt={price.name} />
                        </div>
                      )}
                      <span className={`pricing-status ${price.is_active ? 'active' : 'inactive'}`}>
                        {price.is_active ? t('admin.pricing.statusActive') : t('admin.pricing.statusInactive')}
                      </span>
                      <span className={`pricing-status ${(price.bookable !== 0 && price.bookable !== false) ? 'bookable' : 'not-bookable'}`} style={{ marginLeft: '8px' }}>
                        {(price.bookable !== 0 && price.bookable !== false) ? t('admin.pricing.bookableYes') : t('admin.pricing.bookableNo')}
                      </span>
                    </div>
                    <div className="pricing-actions">
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => openPricingForm(price)}
                      >
                        {t('admin.pricing.edit')}
                      </button>
                      {(price.duration_minutes || 60) !== 30 && (
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => createPricingVariant(price, 30)}
                        >
                          {t('admin.pricing.create30Variant')}
                        </button>
                      )}
                      {(price.duration_minutes || 60) !== 60 && (
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => createPricingVariant(price, 60)}
                        >
                          {t('admin.pricing.create60Variant')}
                        </button>
                      )}
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => deletePricing(price.id)}
                      >
                        {t('admin.pricing.delete')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'vouchers' && (
        <div className="dashboard-section">
          <h2>{t('admin.vouchers.title')}</h2>
          <p className="section-desc">{t('admin.vouchers.intro')}</p>
          <div className="bookings-sub-tabs voucher-bookings-tabs">
            <button
              className={vouchersListSubTab === 'active' ? 'active' : ''}
              onClick={() => setVouchersListSubTab('active')}
            >
              {t('admin.vouchers.tabActive')}
            </button>
            <button
              className={vouchersListSubTab === 'archive' ? 'active' : ''}
              onClick={() => setVouchersListSubTab('archive')}
            >
              {t('admin.vouchers.tabArchive')}
            </button>
          </div>
          <div className="card">
            {vouchers.length === 0 ? (
              <p>{vouchersListSubTab === 'archive' ? t('admin.vouchers.emptyArchive') : t('admin.vouchers.emptyActive')}</p>
            ) : (
              <div className="vouchers-list">
                {vouchers.map(v => (
                  <div key={v.id} className={`voucher-item status-${v.status}`}>
                    <div className="voucher-info">
                      <div className="voucher-row">
                        <strong>{t('admin.vouchers.code')}</strong> <code>{v.code}</code>
                        <span className={`voucher-status-badge ${v.status}`}>
                          {getVoucherStatusLabel(v.status)}
                        </span>
                      </div>
                      <p><strong>{t('admin.vouchers.amount')}</strong> {parseInt(v.amount).toLocaleString(numberLocale)} €</p>
                      <p><strong>{t('admin.vouchers.name')}</strong> {v.customer_name}</p>
                      <p><strong>{t('admin.vouchers.email')}</strong> {v.customer_email}</p>
                      {v.customer_phone && <p><strong>{t('admin.vouchers.phone')}</strong> {v.customer_phone}</p>}
                      {v.notes && <p><strong>{t('admin.vouchers.note')}</strong> {v.notes}</p>}
                      <p><strong>{t('admin.vouchers.created')}</strong> {v.created_at ? new Date(v.created_at).toLocaleString(numberLocale) : '-'}</p>
                      {v.paid_at && <p><strong>{t('admin.vouchers.paid')}</strong> {new Date(v.paid_at).toLocaleString(numberLocale)}</p>}
                      {v.used_at && <p><strong>{t('admin.vouchers.used')}</strong> {new Date(v.used_at).toLocaleString(numberLocale)}</p>}
                    </div>
                    {v.status === 'pending' && vouchersListSubTab === 'active' && (
                      <div className="voucher-actions">
                        <button
                          className="btn btn-success btn-sm"
                          onClick={async () => {
                            try {
                              await axios.put(`/api/voucher/${v.id}/payment`, { status: 'paid' });
                              setSuccess(t('admin.vouchers.paidSuccess'));
                              loadData();
                            } catch (err) {
                              setError(err.response?.data?.error || t('admin.vouchers.errGeneric'));
                            }
                          }}
                        >
                          {t('admin.vouchers.btnPaid')}
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleArchiveVoucher(v.id)}
                        >
                          {t('admin.vouchers.btnArchive')}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <h3 className="voucher-bookings-section-title">{t('admin.vouchers.bookingsTitle')}</h3>
          <p className="section-desc">{t('admin.vouchers.bookingsIntro')}</p>
          <div className="bookings-sub-tabs voucher-bookings-tabs">
            <button
              className={voucherBookingsSubTab === 'active' ? 'active' : ''}
              onClick={() => setVoucherBookingsSubTab('active')}
            >
              {t('admin.vouchers.vbTabActive')}
            </button>
            <button
              className={voucherBookingsSubTab === 'archive' ? 'active' : ''}
              onClick={() => setVoucherBookingsSubTab('archive')}
            >
              {t('admin.vouchers.vbTabArchive')}
            </button>
          </div>
          <div className="card">
            {voucherBookings.length === 0 ? (
              <p>{voucherBookingsSubTab === 'archive' ? t('admin.vouchers.vbEmptyArchive') : t('admin.vouchers.vbEmptyActive')}</p>
            ) : (
              <div className="bookings-list">
                {(() => {
                  const sortedByAge = [...voucherBookings].sort((a, b) => {
                    const tA = a.created_at ? new Date(a.created_at).getTime() : 0;
                    const tB = b.created_at ? new Date(b.created_at).getTime() : 0;
                    return tA - tB;
                  });
                  const rowIndexByBookingId = new Map();
                  sortedByAge.forEach((b, i) => rowIndexByBookingId.set(b.id, i));
                  return voucherBookings.map((booking) => (
                  <BookingItem
                    key={booking.id}
                    booking={booking}
                    rowIndex={rowIndexByBookingId.get(booking.id) ?? 0}
                    formatDate={formatDate}
                    formatHour={formatHour}
                    getPaymentMethodLabel={getPaymentMethodLabel}
                    getPaymentStatusLabel={getPaymentStatusLabel}
                    updatePaymentStatus={updatePaymentStatus}
                    updateBookingCheck={updateBookingCheck}
                    onArchive={voucherBookingsSubTab === 'active' ? handleArchiveVoucherBooking : undefined}
                    onRestore={voucherBookingsSubTab === 'archive' ? handleRestoreVoucherBooking : undefined}
                    isCancelled={false}
                  />
                  ));
                })()}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'flightRequests' && (
        <div className="dashboard-section">
          <h2>{t('admin.flightRequests.title')}</h2>
          <p className="section-desc">{t('admin.flightRequests.intro')}</p>
          <div className="flight-requests-filters">
            <label>{t('admin.flightRequests.statusLabel')}</label>
            <select value={flightRequestsFilterStatus} onChange={(e) => setFlightRequestsFilterStatus(e.target.value)}>
              <option value="">{t('admin.flightRequests.statusAll')}</option>
              <option value="pending">{t('admin.flightRequests.statusPending')}</option>
              <option value="notified">{t('admin.flightRequests.statusNotified')}</option>
              <option value="cancelled">{t('admin.flightRequests.statusCancelled')}</option>
            </select>
          </div>
          <div className="card">
            {flightRequests.length === 0 ? (
              <p>{t('admin.flightRequests.empty')}</p>
            ) : (
              <div className="flight-requests-list">
                {flightRequests.map(fr => (
                  <div key={fr.id} className={`flight-request-item status-${fr.status}`}>
                    <div className="flight-request-info">
                      <p><strong>{t('admin.flightRequests.route', { destination: fr.destination })}</strong></p>
                      <p><strong>{t('admin.flightRequests.date')}</strong> {fr.date}</p>
                      <p><strong>{t('admin.flightRequests.name')}</strong> {fr.name}</p>
                      <p><strong>{t('admin.flightRequests.email')}</strong> <a href={`mailto:${fr.email}`}>{fr.email}</a></p>
                      {fr.phone && <p><strong>{t('admin.flightRequests.phone')}</strong> {fr.phone}</p>}
                      {fr.notes && <p><strong>{t('admin.flightRequests.notes')}</strong> {fr.notes}</p>}
                      <p><strong>{t('admin.flightRequests.created')}</strong> {fr.created_at ? new Date(fr.created_at).toLocaleString(numberLocale) : '-'}</p>
                      <span className={`flight-request-status-badge ${fr.status}`}>{getFlightRequestStatusLabel(fr.status)}</span>
                    </div>
                    {fr.status === 'pending' && (
                      <div className="flight-request-actions">
                        <button
                          className="btn btn-success btn-sm"
                          onClick={async () => {
                            try {
                              await axios.patch(`/api/flight-requests/${fr.id}`, { status: 'notified' });
                              setSuccess(t('admin.flightRequests.successNotified'));
                              loadData();
                            } catch (err) {
                              setError(err.response?.data?.error || t('admin.vouchers.errGeneric'));
                            }
                          }}
                        >
                          {t('admin.flightRequests.btnNotified')}
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={async () => {
                            try {
                              await axios.patch(`/api/flight-requests/${fr.id}`, { status: 'cancelled' });
                              setSuccess(t('admin.flightRequests.successCancelled'));
                              loadData();
                            } catch (err) {
                              setError(err.response?.data?.error || t('admin.vouchers.errGeneric'));
                            }
                          }}
                        >
                          {t('admin.flightRequests.btnCancel')}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'emailTemplates' && canManageEmailTemplates && <EmailTemplatesAdmin />}
    </div>
  );
};

export default AdminDashboard;
